import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClickToCallButton from "@/components/ClickToCallButton";
import IdNudgeButton from "@/components/IdNudgeButton";
import DeliveryExceptionsAdminTab from "@/components/admin/DeliveryExceptionsAdminTab";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import ClickToCallPhone from "@/components/ClickToCallPhone";
import ClickToTextPhone from "@/components/ClickToTextPhone";
import PatientDetailModal from "@/components/PatientDetailModal";
import OrderNotesPanel from "@/components/OrderNotesPanel";
import LabFloStatusChip from "@/components/LabFloStatusChip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Stethoscope, LogOut, Clock, CheckCircle, CheckCircle2, XCircle, Send, Eye, RefreshCw,
  Users, ClipboardList, FileText, FileCheck, User, MapPin, Heart, AlertCircle, Pill, Upload, Download, FolderOpen, MessageCircle, Timer, ExternalLink, Settings, Save, Plane,
  Search, Lock, Unlock, ChevronDown, ChevronRight, Truck, BookOpen, Receipt, Loader2, Repeat, AlertTriangle, ShieldAlert, Building2, Hand, RotateCcw, Copy, FlaskConical
} from "lucide-react";
import { toast } from "sonner";
import StaffChatTab from "@/components/StaffChatTab";
import InstallAppPrompt from "@/components/InstallAppPrompt";
// Kept for any legacy use; new staff login / landing use InstallAppInline.
import { HIPAABanner } from "@/components/HIPAACompliance";
import LabFloStatusBanner from "@/components/LabFloStatusBanner";
import ERXHistoryTab from "@/components/prescriber/ERXHistoryTab";
import PrescriberActivityTab from "@/components/prescriber/PrescriberActivityTab";
import StaffProfileTab from "@/components/prescriber/StaffProfileTab";
import SandboxTab from "@/components/prescriber/SandboxTab";
import PrescriberProfileWarning from "@/components/prescriber/PrescriberProfileWarning";
import AddressVerifyCard from "@/components/AddressVerifyCard";
import StaffTrainingManual from "@/components/StaffTrainingManual";
import TrainingHelpButton from "@/components/TrainingHelpButton";
import UnreadBadge from "@/components/UnreadBadge";
import useTrainingUnread from "@/hooks/useTrainingUnread";
import PinTabButton, { useDefaultLandingTab } from "@/components/PinTabButton";
import ThemeToggle from "@/components/ThemeToggle";
import AutoApproveTab from "@/components/prescriber/AutoApproveTab";
import DDIWarningBanner from "@/components/prescriber/DDIWarningBanner";
import PrescriberDocumentsTab from "@/components/prescriber/PrescriberDocumentsTab";
import MyClinicsTab from "@/components/prescriber/MyClinicsTab";
import RxNoteSaver from "@/components/prescriber/RxNoteSaver";
import RxSeriesModal from "@/components/prescriber/RxSeriesModal";
import MyCommissionCard from "@/components/prescriber/MyCommissionCard";
import TierReviewTab from "@/components/prescriber/TierReviewTab";
import HelpLink from "@/components/HelpLink";
import PrescriberKeyboardShortcuts from "@/components/PrescriberKeyboardShortcuts";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
// Replace floating popup in prescriber dashboard with inline install card.

const STATUS_COLORS = {
  pending_payment: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
  pending_review: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700",
  denied: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700",
};

export default function PrescriberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Capability flags derived from the auth-me payload. Prescribers always
  // pass; admins must carry the `prescribe` cap (or be a super-admin) to
  // see the Write-ERX button + run approve/deny flows.
  const canPrescribe = user?.role === "prescriber" ||
    user?.is_super_admin ||
    (user?.role === "admin" && Array.isArray(user?.capabilities) && user.capabilities.includes("prescribe"));
  const canResetDemo = user?.role === "prescriber" ||
    user?.is_super_admin ||
    (user?.role === "admin" && Array.isArray(user?.capabilities) && user.capabilities.includes("reset_demo_state"));
  const [orders, setOrders] = useState([]);
  const [autoApprovableIds, setAutoApprovableIds] = useState(new Set());
  const [pharmacies, setPharmacies] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [activeTab, setActiveTab] = useState("orders");
  const { unreadCount: trainingUnread, markSeen: markTrainingSeen } = useTrainingUnread(true);
  const defaultLandingTab = useDefaultLandingTab();
  const appliedDefaultOnce = useRef(false);
  useEffect(() => {
    if (appliedDefaultOnce.current) return;
    if (defaultLandingTab && defaultLandingTab !== activeTab) setActiveTab(defaultLandingTab);
    if (defaultLandingTab !== undefined && defaultLandingTab !== null) appliedDefaultOnce.current = true;    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingTab]);

  // Inline help-deeplink: when a <HelpLink /> button is clicked anywhere in
  // this dashboard, switch to the training tab and let the manual handle
  // scroll/expand via the URL hash.
  useEffect(() => {
    const onOpenTraining = () => setActiveTab("training");
    window.addEventListener("glpflo:open-training", onOpenTraining);
    return () => window.removeEventListener("glpflo:open-training", onOpenTraining);
  }, []);
  // Order detail + questionnaire
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [orderPatient, setOrderPatient] = useState(null);
  const [orderReceipt, setOrderReceipt] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [patientDetailId, setPatientDetailId] = useState(null);
  // Which tab to open PatientDetailModal on. Set to "id-verify" from the
  // Order Queue ID-review badge so clinicians land straight on the identity
  // documents; defaults back to "wellness" when opened from a name click.
  const [patientDetailTab, setPatientDetailTab] = useState("wellness");
  // Action modal with ERX form
  const [actionModal, setActionModal] = useState(null);
  // LegitScript gate — the prescriber must click "Reviewed" on the
  // intake questionnaire before Approve/Deny are enabled inside the
  // eRx modal. Resets to false every time the modal opens for a new
  // order. The questionnaire itself is loaded into `actionQuestionnaire`
  // so the modal shows the full answers before the gate is unlocked.
  const [qReviewAcked, setQReviewAcked] = useState(false);
  const [actionQuestionnaire, setActionQuestionnaire] = useState(null);
  const [markingQReviewed, setMarkingQReviewed] = useState(false);
  // Step 1 gate — patient's uploaded government ID must be marked
  // Verified before questionnaire review unlocks. Server-side also
  // enforces this on PUT /orders/{id}/action (428 id_verification_required).
  const [idVerification, setIdVerification] = useState(null);
  const [loadingIdVerification, setLoadingIdVerification] = useState(false);
  // Auto-selected state license for the eRx (previewed from the backend
  // so the prescriber can visually verify BEFORE clicking Approve that
  // the correct state-specific license is being stamped on the Rx).
  const [prescribingLicense, setPrescribingLicense] = useState(null);
  const [loadingLicense, setLoadingLicense] = useState(false);
  const [ddiAck, setDdiAck] = useState({ all_high_acknowledged: true, acked_keys: [], high_keys: [] });
  const [erxForm, setErxForm] = useState({ medication_name: "", dosage: "", quantity: "", refills: "0", directions: "", diagnosis: "", rx_notes: "", notes: "" });
  // Tracks which Quick Rx Template the prescriber just clicked so the button
  // gets a purple ring + gradient — makes it obvious which template is loaded
  // into the eRx form below.
  const [selectedRxTemplateId, setSelectedRxTemplateId] = useState(null);
  const [actionPharmacy, setActionPharmacy] = useState("");
  // Prescriber opt-in: sign with their saved touch-signature (Employee
  // Profile → Signature) instead of the plain attestation checkbox.
  const [useSavedSignature, setUseSavedSignature] = useState(false);
  const [savedSignature, setSavedSignature] = useState("");
  const [processing, setProcessing] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  // Rx-series follow-up modal (opens after approval for multi-month tiers)
  const [seriesModalOrder, setSeriesModalOrder] = useState(null);
  // Tier-review queue count (badge on sidebar)
  const [tierReviewCount, setTierReviewCount] = useState(0);
  // Patient detail
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);
  const [patientEditMode, setPatientEditMode] = useState(false);
  const [patientEditForm, setPatientEditForm] = useState({});
  // Verification gate for the prescriber-side patient edit form. Address must
  // be verified before "Save patient" will write the changes back.
  const [patientAddrStatus, setPatientAddrStatus] = useState("verified");
  const [patientSavePending, setPatientSavePending] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  // Patient records search (search by last/first name or DOB)
  const [patientSearch, setPatientSearch] = useState("");
  // Pre-screened orders are always included in the main queue — they live in
  // status=pending_review and surface alongside the rest, carrying the PS
  // badge so clinicians can still spot them at a glance.
  // Above-starter filter — when ON, queue is restricted to orders where the
  // patient explicitly requested a strength above the safe starter dose AND
  // acknowledged the $25-on-denial warning. This is the highest-risk subset
  // for clinical attention.
  const [aboveStarterOnly, setAboveStarterOnly] = useState(false);
  // Unverified-only filter — when ON the queue shows only rows whose
  // patient is missing or pending ID verification. Client-side only —
  // operates on already-loaded data via id_verification_status (computed
  // server-side in /api/orders enrichment).
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  // Stats
  const [stats, setStats] = useState({ pending: 0, approved: 0, denied: 0 });
  // Dose presets
  const [dosePresets, setDosePresets] = useState([]);
  const [rxTemplates, setRxTemplates] = useState([]);
  // My-clinics list + dispense-template list for the eRx modal
  const [myClinics, setMyClinics] = useState([]);
  const [dispenseTemplates, setDispenseTemplates] = useState([]);
  // Ship-to-clinic toggle for the eRx in-flight modal
  const [shipToClinic, setShipToClinic] = useState(false);
  const [shipClinicId, setShipClinicId] = useState("");
  // Deny flow state — when the prescriber clicks "Deny Order", an inline
  // panel reveals (denyMode=true) instead of immediately calling the API,
  // so they can attach a structured reason. For the "insufficient_documentation"
  // reason, ticking the boxes auto-generates a patient-facing email that
  // tells them which fields to re-submit with more detail.
  const [denyMode, setDenyMode] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [denyMissingFields, setDenyMissingFields] = useState([]);
  const [denyExtra, setDenyExtra] = useState("");
  const isCS = user?.role === "customer_service";
  // Queue scope: "all" (legacy view — every incoming order) or "mine"
  // (sticky-assigned-to-me + unassigned orders in tiers I'm authorized
  // to fill). Persists across reloads via localStorage so PA/NPs land
  // back on My Queue. MDs default to "all" since they can fill anything.
  const isMd = ((user?.prescriber_type || "").toLowerCase() === "physician"
    || (user?.prescriber_type || "").toLowerCase() === "md"
    || (user?.prescriber_type || "").toLowerCase() === "do"
    || user?.role === "admin");
  const [queueScope, setQueueScope] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("glpflo_queue_scope") : null;
    if (stored === "mine" || stored === "all") return stored;
    return isMd ? "all" : "mine";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("glpflo_queue_scope", queueScope);
  }, [queueScope]);

  // Triage mode — focuses the queue on rows that match at least one
  // attention filter, sorted by the prescriber's saved priority order.
  // Filter set comes from the backend (canonical keys) so new filters
  // can be added in code without needing every user to re-save.
  const [triageMode, setTriageMode] = useState(() => {
    return typeof window !== "undefined" && localStorage.getItem("glpflo_triage_mode") === "1";
  });
  const [triageOrder, setTriageOrder] = useState([
    "sla_reassigned", "halfway_warned", "above_starter", "patient_note", "pending_review",
  ]);
  const [showTriageEditor, setShowTriageEditor] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("glpflo_triage_mode", triageMode ? "1" : "0");
  }, [triageMode]);

  // Load this user's saved triage filter order on mount.
  useEffect(() => {
    if (user?.role !== "prescriber" && user?.role !== "admin") return;
    API.get("/prescriber/triage-preferences")
      .then(({ data }) => { if (Array.isArray(data?.filter_order)) setTriageOrder(data.filter_order); })
      .catch(() => {});
  }, [user?.role]);

  const moveTriageFilter = (idx, direction) => {
    setTriageOrder((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      API.put("/prescriber/triage-preferences", { filter_order: next }).catch(() => {});
      return next;
    });
  };

  // Pure-function evaluator: returns the first matching filter key for
  // an order (priority by current triageOrder), or null if no filter
  // matches. Drives both the visibility filter AND the sort priority.
  const matchedTriageKey = (order) => {
    const checks = {
      sla_reassigned: () => order.assignment_source === "sla_reassigned",
      halfway_warned: () => !!order.sla_warning_sent_at && order.prescriber_id === user?._id
        && order.assignment_source !== "sla_reassigned",
      above_starter: () => !!order.dose_request_above_starter,
      patient_note: () => !!order.patient_note && !order.patient_note_acknowledged,
      pending_review: () => order.status === "pending_review",
    };
    for (const key of triageOrder) {
      const fn = checks[key];
      if (fn && fn()) return key;
    }
    return null;
  };
  // Clock in/out
  const [clockedIn, setClockedIn] = useState(false);
  const [clockEntry, setClockEntry] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const queueParam = queueScope === "mine" ? "&queue=mine" : "";
      const [ordersRes, pharmRes, patientsRes, autoRes] = await Promise.all([
        API.get(`/orders?status=${statusFilter}${aboveStarterOnly ? "&above_starter_only=1" : ""}${queueParam}`),
        API.get("/pharmacy-tenants"),
        API.get("/prescriber/patients"),
        API.get("/orders/auto-approvable").catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setPharmacies(pharmRes.data);
      setPatients(patientsRes.data);
      setAutoApprovableIds(new Set((autoRes.data || []).map((o) => o.id)));
      const [pRes, aRes, dRes] = await Promise.all([
        API.get(`/orders?status=pending_review${queueParam}`),
        API.get(`/orders?status=approved${queueParam}`),
        API.get(`/orders?status=denied${queueParam}`),
      ]);
      setStats({ pending: pRes.data.length, approved: aRes.data.length, denied: dRes.data.length });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [statusFilter, aboveStarterOnly, queueScope]);

  // Demo-state reset — re-runs /app/backend/scripts/seed_investor_demo_orders.py
  // via the staff-only POST /api/admin/demo/reset endpoint. Available to admins
  // and prescribers so the presenter can re-seed mid-pitch without an SSH
  // session. Guarded with a single confirm() so a clinician doesn't trigger
  // it by accident.
  const [resettingDemo, setResettingDemo] = useState(false);
  const resetDemoState = useCallback(async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Reset investor-demo state?\n\nThis re-runs the seed script and refreshes all 10 demo orders (Alyssa Monroe, Brandon Cole, etc.). It does NOT touch real patient data — only the *@investor-demo.glpflo.test accounts.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'warn' }))) return;
    setResettingDemo(true);
    try {
      const { data } = await API.post("/admin/demo/reset");
      if (data?.ok) {
        toast.success(`Demo state reset in ${data.elapsed_ms} ms`, {
          description: `Refreshed 10 demo orders. Reset by ${data.reset_by_role}.`,
        });
        loadData();
      } else {
        toast.error("Demo reset failed", { description: data?.error || "Check backend logs." });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo reset failed");
    } finally {
      setResettingDemo(false);
    }
  }, [loadData]);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await API.get("/dose-presets");
      setDosePresets(data);
    } catch (e) { console.error("Failed to load presets:", e); }
  }, []);

  const loadRxTemplates = useCallback(async () => {
    try {
      const { data } = await API.get("/rx-templates");
      setRxTemplates(data.filter(t => t.active));
    } catch (e) { console.error("Failed to load rx templates:", e); }
  }, []);

  const loadMyClinics = useCallback(async () => {
    try {
      const { data } = await API.get("/prescriber/clinics");
      setMyClinics(data || []);
    } catch (e) { console.error("Failed to load clinics:", e); }
  }, []);

  const loadDispenseTemplates = useCallback(async () => {
    try {
      const { data } = await API.get("/dispense-templates");
      setDispenseTemplates(data || []);
    } catch (e) { console.error("Failed to load dispense templates:", e); }
  }, []);

  const loadClockStatus = useCallback(async () => {
    try {
      const { data } = await API.get("/timeclock/status");
      setClockedIn(data.clocked_in);
      setClockEntry(data.entry);
    } catch (e) { console.error("Clock status error:", e); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadPresets(); loadRxTemplates(); loadClockStatus(); loadMyClinics(); loadDispenseTemplates(); }, [loadPresets, loadRxTemplates, loadClockStatus, loadMyClinics, loadDispenseTemplates]);
  // Pull the prescriber's stored e-signature (if any) so the sign-off modal
  // can offer "sign with saved signature" as an alternative to the checkbox.
  useEffect(() => {
    API.get("/me/signature")
      .then(({ data }) => setSavedSignature(data?.data_url || ""))
      .catch(() => {});
  }, []);
  useEffect(() => { if (activeTab === "training") markTrainingSeen(); }, [activeTab, markTrainingSeen]);

  // Refresh tier-review badge whenever we land on any tab + every 60s.
  useEffect(() => {
    let alive = true;
    const fetchCount = () => {
      API.get("/prescriber/tier-review-queue")
        .then(({ data }) => alive && setTierReviewCount(data?.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [activeTab]);

  // Listen for `glpflo:apply-rx-suggestion` events fired from the wellness
  // panel "Apply" button. Opens the patient's pending order with the matching
  // Rx template pre-applied + suggestion note prepended to rx_notes. Pure UI
  // shortcut — prescriber still reviews and submits as usual.
  useEffect(() => {
    const onApply = async (e) => {
      const detail = e.detail || {};
      const orderId = detail.order_id;
      const tpl = detail.template;
      const note = detail.suggestion_note || "";
      const title = detail.suggestion_title || "Wellness suggestion";
      if (!orderId || !tpl) return;
      try {
        const { data: order } = await API.get(`/orders/${orderId}`);
        setActionModal(order);
        setErxForm({
          medication_name: tpl.medication_name || tpl.name || "",
          dosage: tpl.dosage || "",
          quantity: tpl.quantity || "1",
          quantity_numeric: 1,
          quantity_unit: "mL",
          route_form: "Subcutaneous Injection",
          refills: "0",
          directions: tpl.directions || "",
          diagnosis: tpl.diagnosis || "",
          rx_notes: `[${title}]\n${note}\n\n${tpl.rx_notes || ""}`.trim(),
          notes: "",
        });
      } catch (err) {
        toast.error("Couldn't open order — it may be already actioned");
      }
    };
    window.addEventListener("glpflo:apply-rx-suggestion", onApply);
    return () => window.removeEventListener("glpflo:apply-rx-suggestion", onApply);
  }, []);

  const handleClockPunch = async () => {
    setClockLoading(true);
    try {
      const { data } = await API.post("/timeclock/punch");
      setClockedIn(data.action === "clock_in");
      setClockEntry(data.action === "clock_in" ? data.entry : null);
      toast.success(data.action === "clock_in" ? "Clocked in!" : "Clocked out!");
    } catch (e) { toast.error("Clock punch failed"); } finally { setClockLoading(false); }
  };

  const resendToPharmacy = async (order) => {
    if (!order?.id) return;
    setResendingId(order.id);
    try {
      const { data } = await API.post(`/orders/${order.id}/resend-pharmacy`);
      if (data.dispatched) {
        toast.success("Resent — transmitted to Polomar");
      } else if (data.blocked && Array.isArray(data.missing) && data.missing.length) {
        toast.warning(`Not sent — missing: ${data.missing.join(", ")}. Fix it and resend.`);
      } else if (data.not_configured) {
        toast.warning("Pharmacy (Polomar) is inactive — nothing sent. Activate it in Admin → Pharmacy.");
      } else {
        toast.error(data.message || "Resend failed — check Admin → Pharmacy.");
      }
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResendingId(null);
    }
  };


  const viewOrderDetails = async (order) => {
    // Acquire a soft pessimistic lock so a second prescriber can't open
    // this same order. Admins bypass the lock check on the server, and
    // the holder is auto-extended on every 60s heartbeat (set up below).
    if (user?.role !== "admin") {
      try {
        await API.post(`/orders/${order.id}/lock`);
      } catch (err) {
        if (err?.response?.status === 423) {
          const d = err.response.data?.detail || {};
          toast.error(d.message || `${d.holder_name || "Another prescriber"} is reviewing this order — try again in a few minutes.`);
          return;
        }
        // Non-locking errors (404, 500, network) shouldn't block review.
        console.warn("lock acquire failed (non-blocking):", err);
      }
    }
    setSelectedOrder(order);
    setOrderReceipt(null);
    try {
      const [qRes, pRes, rRes] = await Promise.all([
        API.get(`/questionnaires/${order.questionnaire_id}`).catch(() => ({ data: null })),
        API.get(`/prescriber/patients/${order.patient_id}`).catch(() => ({ data: null })),
        API.get(`/receipts/by-order/${order.id}`).catch(() => ({ data: null })),
      ]);
      setQuestionnaire(qRes.data);
      setOrderPatient(pRes.data?.patient || null);
      setOrderReceipt(rRes.data || null);
    } catch { setQuestionnaire(null); setOrderPatient(null); setOrderReceipt(null); }
  };

  // Heartbeat the lock while the modal is open (server TTL is 5 min, we
  // ping every 60s). Stops + releases on modal close.
  useEffect(() => {
    if (!selectedOrder?.id || user?.role === "admin") return undefined;
    const intervalId = setInterval(() => {
      API.post(`/orders/${selectedOrder.id}/lock/heartbeat`).catch(() => {});
    }, 60000);
    return () => {
      clearInterval(intervalId);
      // Best-effort release on close — also fires browser-close via TTL.
      API.post(`/orders/${selectedOrder.id}/lock/release`).catch(() => {});
    };
  }, [selectedOrder?.id, user?.role]);

  // Map of {order_id: {holder_id, holder_name, expires_at, is_mine}} for
  // every currently-live lock. Updated by the 30s poller below.
  const [activeLocks, setActiveLocks] = useState({});

  // Poll the live-locks map every 30s so the queue can render a tiny
  // "🔒 In review by Dr. X" pill on each row that's actively being
  // reviewed — preventing wasted clicks discovering an order is taken.
  // Fires immediately on mount so the first paint shows accurate state.
  useEffect(() => {
    let mounted = true;
    const loadLocks = () => {
      API.get("/orders/locks/active")
        .then(({ data }) => { if (mounted) setActiveLocks(data || {}); })
        .catch(() => {});
    };
    loadLocks();
    const id = setInterval(loadLocks, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // One-shot SLA notifications: the hourly sweep pushes two kinds —
  //   • kind='warning'    → at half-threshold, this prescriber STILL holds
  //                         the order; gentle reminder to act.
  //   • kind='reassigned' → at full-threshold the order was yanked.
  // Both get a toast on next dashboard load; mark-as-seen on read so a
  // refresh doesn't re-toast.
  useEffect(() => {
    if (user?.role !== "prescriber") return;
    API.get("/admin/sla-notifications/mine")
      .then(({ data }) => {
        (data?.unseen || []).forEach((n) => {
          if (n.kind === "warning") {
            toast.warning(
              `⏰ "${n.medication_name || "An order"}" has been waiting ${Math.round(n.age_hours)}h. Please review — it'll auto-reassign in ${Math.max(0, Math.round(n.hours_remaining || 0))}h.`,
              { duration: 10000 },
            );
          } else {
            toast.warning(
              `⏱️ One of your orders (${n.medication_name || "order"}, ${n.payment_type || "tier"}) was idle ${Math.round(n.age_hours)}h and re-routed to the general queue.`,
              { duration: 8000 },
            );
          }
        });
      })
      .catch(() => {});
  }, [user?.role]);

  const claimOrder = async (order) => {
    try {
      await API.post(`/orders/${order.id}/claim`);
      toast.success(`Claimed — ${order.medication_name || "order"} is now in your queue`);
      loadData();
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail || {};
      if (status === 409) toast.error(d.message || "Already claimed by another prescriber");
      else if (status === 423) toast.error(d.message || "Locked — someone is reviewing this order");
      else if (status === 403) toast.error(d.message || "Not authorized to fill this tier");
      else toast.error(d.message || "Claim failed");
    }
  };

  const downloadOrderReceipt = async () => {
    if (!orderReceipt?.session_id) return;
    setDownloadingReceipt(true);
    try {
      const res = await API.get(`/receipts/${encodeURIComponent(orderReceipt.session_id)}.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GLPFlo-receipt-${orderReceipt.receipt_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Receipt download failed");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  // Opens the eRx PDF endpoint in a new tab. Instead of leaking the long-lived
  // JWT in localStorage + URL, we mint a 5-minute PDF-scoped signed URL from
  // the backend and open that. The main access token stays in an httpOnly
  // cookie and never touches JavaScript.
  //
  // IMPORTANT: We open the blank tab SYNCHRONOUSLY inside the click handler
  // so Chrome treats it as a user-initiated pop-up (which it is). If we wait
  // for the API call to finish first, Chrome sees a "programmatic" popup and
  // blocks it even when the user has allowed pop-ups for the site.
  const openErxPdf = async (erxId) => {
    if (!erxId) {
      toast.error("No eRx record linked to this order yet");
      return;
    }
    const win = window.open("about:blank", "_blank", "noopener,noreferrer");
    if (!win) {
      toast.error("Browser blocked the popup — allow popups for this site to view eRx PDFs");
      return;
    }
    try {
      const { data } = await API.post(`/erx/${erxId}/pdf-url`);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      win.location.href = `${base}${data.path}`;
    } catch (e) {
      try { win.close(); } catch { /* ignore */ }
      toast.error(e.response?.data?.detail || "Unable to open eRx PDF");
    }
  };

  const openActionModal = (order) => {
    setActionModal(order);
    // Reset the LegitScript review gate every time the modal opens.
    // If the order was already reviewed (e.g. prescriber re-opened it
    // after saving a draft) we pre-check the box so they don't have
    // to re-attest — but we still show the questionnaire.
    setQReviewAcked(!!order.questionnaire_reviewed || !!order.is_sandbox);
    setActionQuestionnaire(null);
    // Sandbox orders bypass BOTH identity + prescribing-license checks
    // at the UI layer so a prescriber can practise every scenario even
    // if the deployed backend hasn't picked up the sandbox short-
    // circuits yet. The backend has the same guards for defence-in-
    // depth, but this stub keeps the modal flowing regardless.
    if (order?.is_sandbox) {
      const shipState = (order.shipping_address?.state || order.patient_state || "").toUpperCase();
      setPrescribingLicense({
        order_id: order.id,
        patient_state: shipState,
        has_active_license: true,
        sandbox: true,
        license: {
          state: shipState,
          license_number: `SBX-${shipState || "XX"}`,
          npi_number: user?.npi_number || "",
          expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
          expiring_soon: false,
          enabled: true,
          is_active: true,
        },
      });
      setLoadingLicense(false);
      setIdVerification({
        patient_id: order.patient_id,
        patient_name: order.patient_name,
        verification_method: "sandbox_bypass",
        has_id_card: true,
        has_video: true,
        verified: true,
        id_type: "drivers_license",
        id_number_last4: "0000",
        verified_at: new Date().toISOString(),
        sandbox: true,
      });
      setLoadingIdVerification(false);
    } else {
      // Auto-select the correct state license for this order's ship-to
      // state (LegitScript / medical-board compliance). Server-side is
      // already enforcing this on eRx doc build; the modal previews it
      // read-only so the prescriber can visually confirm before Approve.
      setPrescribingLicense(null);
      setLoadingLicense(true);
      (async () => {
        try {
          const { data } = await API.get(`/orders/${order.id}/prescribing-license`);
          setPrescribingLicense(data);
        } catch {
          setPrescribingLicense(null);
        } finally {
          setLoadingLicense(false);
        }
      })();
      // Step 1: load the patient's uploaded ID + verification status so
      // the modal can render the "Verify ID" gate ahead of the intake
      // review. Uses the admin ID-verification endpoint which returns
      // download URLs + verified flag.
      setIdVerification(null);
      setLoadingIdVerification(true);
      (async () => {
        try {
          const { data } = await API.get(`/admin/users/${order.patient_id}/id-verification`);
          setIdVerification(data);
        } catch {
          setIdVerification(null);
        } finally {
          setLoadingIdVerification(false);
        }
      })();
    }
    // Hydrate the questionnaire so the review panel isn't empty.
    // Prefer the linked db.questionnaires row (`order.questionnaire_id`)
    // and fall back to the responses embedded on the order itself
    // (seeded demo orders use this fallback). Sandbox orders ALWAYS
    // ship their mock answers embedded, so we skip the fetch entirely
    // to avoid any "Loading…" flash.
    if (order?.is_sandbox && (order.questionnaire_responses || order.answers)) {
      setActionQuestionnaire({
        id: order.id,
        answers: order.questionnaire_responses || order.answers,
        submitted_at: order.created_at,
        patient_name: order.patient_name,
        sandbox: true,
      });
    } else {
      (async () => {
        try {
          if (order.questionnaire_id) {
            const { data } = await API.get(`/questionnaires/${order.questionnaire_id}`);
            setActionQuestionnaire(data);
            return;
          }
        } catch { /* fall through to embedded responses */ }
        const embedded = order.questionnaire_responses || order.answers || null;
        setActionQuestionnaire({
          id: order.id,
          // Always set a non-null object so the modal's "Loading…" state
          // resolves even if nothing is available — the panel then shows
          // "No questionnaire on file." instead of spinning.
          answers: embedded,
          submitted_at: order.created_at,
          patient_name: order.patient_name,
        });
      })();
    }
    // Pre-fill the eRx form from any clinical defaults the order already
    // carries (dosage_requested, directions_template, diagnosis_template,
    // rx_notes_template, quantity, refills). Seeded investor-demo orders
    // populate all of these so the modal opens fully-formed for a clean
    // pitch demo. Production orders typically only have dosage_requested.
    const tierTag = order.tier_label ? `[Tier: ${order.tier_label}]\n` : "";
    setErxForm({
      medication_name: order.medication_name || "",
      dosage: order.dosage_requested || order.medication_strength || "",
      quantity: order.quantity || String(order.vial_count || order.supply_units || 1),
      quantity_numeric: order.quantity_numeric || order.vial_count || order.supply_units || 1,
      quantity_unit: order.quantity_unit || "mL",
      route_form: order.route_form || "Subcutaneous Injection",
      refills: order.refills || "0",
      directions: order.directions_template || "",
      diagnosis: order.diagnosis_template || "",
      rx_notes: `${tierTag}${order.rx_notes_template || ""}`.trim(),
      notes: "",
    });
    // Auto-pick the preferred pharmacy (Polomar) when nothing is pre-set on
    // the order. Falls back to the first item if no polomar row exists.
    const preferred = (pharmacies || []).find(
      (p) => (p.slug || "").toLowerCase() === "polomar",
    );
    setActionPharmacy(order.pharmacy_tenant_id || preferred?.id || "");
    setUseSavedSignature(false);
    // Reset ship-to toggle on every open; pre-select the prescriber's default
    // clinic so a one-click "Ship to Clinic" flow still picks the right address.
    setShipToClinic(false);
    const def = (myClinics || []).find((c) => c.is_default);
    setShipClinicId(def ? def.id : ((myClinics || [])[0]?.id || ""));
  };

  /**
   * One-click "Recommend starter instead" — pre-opens the Write ERX modal
   * with the safe starter strength pre-filled in the dosage field and a
   * templated clinical note explaining the recommendation. The prescriber
   * still has to review + click "Approve" — we just shave the typing.
   */
  const openRecommendStarter = (order) => {
    const starter = order.starter_strength || "";
    setActionModal(order);
    setErxForm({
      medication_name: order.medication_name || "",
      dosage: starter,
      quantity: "1",
      quantity_numeric: 1,
      quantity_unit: "mL",
      route_form: "Subcutaneous Injection",
      refills: "0",
      directions: starter
        ? `Inject ${starter} subcutaneously once weekly. Increase as tolerated per titration schedule.`
        : "",
      diagnosis: "",
      rx_notes: starter && order.requested_strength
        ? `Patient requested ${order.requested_strength} (above safe starter). Recommending titration start at ${starter} for clinical safety; patient may titrate up per protocol after tolerability is established.`
        : "",
      notes: "",
    });
    const preferredRx = (pharmacies || []).find(
      (p) => (p.slug || "").toLowerCase() === "polomar",
    );
    setActionPharmacy(order.pharmacy_tenant_id || preferredRx?.id || "");
    setUseSavedSignature(false);
    toast.info(`Pre-filled with starter dose ${starter || "(see medication strengths)"} — review and approve to send.`);
  };

  const handleAction = async (action) => {
    setProcessing(true);
    try {
      const payload = {
        action,
        notes: erxForm.notes || null,
        pharmacy_tenant_id: actionPharmacy || null,
      };
      if (action === "deny") {
        payload.deny_reason = denyReason || null;
        // Compose a patient-facing message based on the chosen reason so
        // the deny email turns into a constructive resubmission path
        // rather than a vague "your order was denied" notice.
        if (denyReason === "insufficient_documentation") {
          const labels = {
            pharmacy: "where you got your previous medication",
            prescriber: "who prescribed it",
            dose: "your last dose + cadence",
            duration: "how long you were on it",
          };
          const missingText = denyMissingFields.length
            ? denyMissingFields.map((k) => `• ${labels[k] || k}`).join("\n")
            : "• more detail in each documentation field";
          payload.notes = (
            `Your above-starter dose request needs more documentation before your prescriber can approve it. Please re-submit your order and include:\n\n${missingText}\n\n` +
            (denyExtra ? `Additional note from your prescriber: ${denyExtra}\n\n` : "") +
            "Once we have these details we can review your request again. The $25 consultation fee from this denial will be credited to your re-order."
          );
        } else if (denyReason && !payload.notes) {
          // Fall back to a generic message keyed off the picked reason.
          const generic = {
            above_starter_clinical: "Based on clinical guidelines, we cannot start you at the strength you requested. Your prescriber recommends beginning at the safe starter dose. Please re-order at the starter strength.",
            missing_labs: "Recent lab work is required before your prescriber can authorize this dose. Please upload your most recent labs and re-submit your order.",
            out_of_state: "Unfortunately your prescriber is not licensed to prescribe in your state. Please contact support.",
            other: "Your order was denied. Please contact support if you'd like more detail.",
          };
          payload.notes = generic[denyReason] || null;
        }
      }
      if (action === "approve") {
        payload.ddi_acknowledged_keys = ddiAck.acked_keys || [];
        payload.ship_to_clinic = !!shipToClinic;
        payload.clinic_id = shipToClinic ? (shipClinicId || null) : null;
        payload.use_saved_signature = !!useSavedSignature;
        if (shipToClinic && !shipClinicId) {
          toast.error("Pick a clinic to ship to, or switch back to 'Ship to Patient'.");
          setProcessing(false);
          return;
        }
        payload.erx_details = {
          medication_name: erxForm.medication_name,
          dosage: erxForm.dosage,
          quantity: erxForm.quantity,
          quantity_numeric: erxForm.quantity_numeric,
          quantity_unit: erxForm.quantity_unit,
          route_form: erxForm.route_form,
          refills: erxForm.refills,
          directions: erxForm.directions,
          diagnosis: erxForm.diagnosis,
          rx_notes: erxForm.rx_notes,
        };
      }
      const { data: actionResp } = await API.put(`/orders/${actionModal.id}/action`, payload);
      if (action === "approve") {
        const ph = actionResp?.pharmacy || {};
        if (ph.dispatched) {
          toast.success("Order approved — eRx transmitted to Polomar");
        } else if (ph.blocked && Array.isArray(ph.missing) && ph.missing.length) {
          toast.warning(`Order approved, but NOT sent to Polomar — missing: ${ph.missing.join(", ")}. Fix it, then resend from Admin → Pharmacy.`);
        } else if (ph.not_configured) {
          toast.warning("Order approved, but the pharmacy (Polomar) is inactive — nothing was transmitted. Activate it in Admin → Pharmacy, then resend.");
        } else if (actionResp?.status === "pending_patient_approval") {
          toast.success("Approved — awaiting patient approval of the strength change");
        } else {
          toast.warning("Order approved, but the pharmacy transmission did not confirm. Check Admin → Pharmacy and resend if needed.");
        }
      } else {
        toast.success("Order denied");
      }
      // For multi-month tiers, open the series modal so the prescriber can
      // queue future orders in one flow. Skip silently if not eligible or
      // if the order already has a series.
      const approvedOrder = actionModal;
      const tier = (approvedOrder?.payment_type || "").toLowerCase();
      const eligible = ["one_time", "subscription", "royal_90"].includes(tier);
      if (action === "approve" && eligible && !approvedOrder?.series_id) {
        setSeriesModalOrder({
          ...approvedOrder,
          erx_details: payload.erx_details, // pass the freshly-prescribed dose for seeding the ladder
        });
      }
      setActionModal(null);
      setErxForm({ medication_name: "", dosage: "", quantity: "", quantity_numeric: 1, quantity_unit: "mL", route_form: "Subcutaneous Injection", refills: "0", directions: "", diagnosis: "", rx_notes: "", notes: "" });
      setSelectedRxTemplateId(null);
      setActionPharmacy("");
      setShipToClinic(false);
      setShipClinicId("");
      setDenyMode(false);
      setDenyReason("");
      setDenyMissingFields([]);
      setDenyExtra("");
      loadData();
    } catch (e) {
      // Surface state-licensing block from the backend pharmacy guard with a
      // clear, actionable warning ("Pharmacy not licensed to ship to NY —
      // pick a different pharmacy").
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "pharmacy_not_licensed_for_state") {
        toast.error(detail.message, { duration: 8000 });
        setActionModal((prev) => prev); // keep modal open so prescriber can switch pharmacy
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(`Failed to ${action} order`);
      }
    } finally { setProcessing(false); }
  };

  const viewPatientDetail = async (patientId) => {
    try {
      const { data } = await API.get(`/prescriber/patients/${patientId}`);
      setPatientDetail(data);
      setSelectedPatient(data.patient);
      setPatientEditMode(false);
      setPatientEditForm({});
      setExpandedOrderId(null);
    } catch { toast.error("Failed to load patient"); }
  };

  const handlePatientSave = async () => {
    if (!selectedPatient?._id && !selectedPatient?.id) return;
    const pid = selectedPatient._id || selectedPatient.id;
    // Skip the gate if no address fields were touched in this edit.
    const addressTouched = ["address_street", "address_city", "address_state", "address_zip"]
      .some(k => patientEditForm[k] && patientEditForm[k] !== selectedPatient[k]);
    if (addressTouched && patientAddrStatus !== "verified") {
      toast.error("Please verify the new address first — we can't ship to addresses that aren't verified.");
      return;
    }
    setPatientSavePending(true);
    try {
      // Persist via the existing admin user-update endpoint (works for staff)
      await API.put(`/admin/users/${pid}`, patientEditForm);
      toast.success("Patient record updated");
      // Refresh the dialog data
      const { data } = await API.get(`/prescriber/patients/${pid}`);
      setPatientDetail(data);
      setSelectedPatient(data.patient);
      setPatientEditMode(false);
      setPatientEditForm({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setPatientSavePending(false);
    }
  };

  // Filter patients by last name, first name, or DOB (MM/DD/YYYY or YYYY-MM-DD).
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const last = name.split(" ").slice(-1)[0] || "";
      const first = name.split(" ")[0] || "";
      const dobIso = p.date_of_birth || "";
      let dobUS = "";
      if (dobIso && dobIso.length >= 10) {
        const [y, m, d] = dobIso.slice(0, 10).split("-");
        if (y && m && d) dobUS = `${m}/${d}/${y}`;
      }
      return (
        name.includes(q) ||
        last.startsWith(q) ||
        first.startsWith(q) ||
        dobIso.startsWith(q) ||
        dobUS.startsWith(q)
      );
    });
  }, [patients, patientSearch]);

  // Debounced server-side patient search. Falls back to in-memory filtering
  // for tiny queries; once you cross 2 chars we hit /prescriber/patients?q=
  // so the search keeps working even when the patient list grows past the
  // 500-record default cap.
  useEffect(() => {
    const q = patientSearch.trim();
    const handle = setTimeout(async () => {
      try {
        const url = q.length >= 2
          ? `/prescriber/patients?q=${encodeURIComponent(q)}`
          : "/prescriber/patients";
        const { data } = await API.get(url);
        setPatients(data);
      } catch (e) { console.error("Patient search failed:", e); }
    }, 350);
    return () => clearTimeout(handle);
  }, [patientSearch]);

  const handleLogout = async () => { await logout(); navigate("/cliniclogin"); };

  const groupedTemplates = useMemo(() => ({
    semaglutide: rxTemplates.filter(t => t.category === "semaglutide"),
    tirzepatide: rxTemplates.filter(t => t.category === "tirzepatide"),
    other: rxTemplates.filter(t => !["semaglutide", "tirzepatide"].includes(t.category)),
  }), [rxTemplates]);

  // Visually group orders by patient_id so multi-medication regimens are
  // approved together. The prescriber sees adjacent rows for the same
  // patient with a "Regimen: N orders" badge — eliminates the risk of
  // approving one medication and forgetting the patient also requested
  // another (e.g. Sema + Wegovy refills, or Tirz + B12). We only group
  // pending_review orders; approved/denied lists keep their default
  // chronological order. Bulk action is intentionally NOT added (per
  // product decision: visual grouping only — each ERX is still written
  // individually so clinical judgement applies to each line).
  const groupedOrders = useMemo(() => {
    if (statusFilter !== "pending_review") return orders;
    const byPatient = new Map();
    for (const o of orders) {
      const pid = o.patient_id || "__no_pid__";
      if (!byPatient.has(pid)) byPatient.set(pid, []);
      byPatient.get(pid).push(o);
    }
    // Sort each patient's orders by created_at asc so the patient's first
    // request shows first within their cluster.
    for (const list of byPatient.values()) {
      list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    }
    // Sort patient clusters so:
    //   - multi-order patients float to the top (sibling-aware queue)
    //   - within tie, newest order across the cluster determines order
    const clusters = Array.from(byPatient.entries()).sort((a, b) => {
      const aMulti = a[1].length > 1 ? 0 : 1;
      const bMulti = b[1].length > 1 ? 0 : 1;
      if (aMulti !== bMulti) return aMulti - bMulti;
      const aLatest = a[1][a[1].length - 1]?.created_at || "";
      const bLatest = b[1][b[1].length - 1]?.created_at || "";
      return bLatest.localeCompare(aLatest);
    });
    const flat = [];
    for (const [pid, list] of clusters) {
      const groupSize = list.length;
      const hasAboveStarter = list.some((o) => o.dose_request_above_starter);
      const aboveCount = list.filter((o) => o.dose_request_above_starter).length;
      list.forEach((o, idx) => {
        flat.push({
          ...o,
          __groupSize: groupSize,
          __groupIndex: idx,
          __groupKey: pid,
          __groupFirst: idx === 0 && groupSize > 1,
          __groupLast: idx === groupSize - 1 && groupSize > 1,
          // Clinical-safety alert: cluster of 2+ orders AND at least one is
          // an above-starter request. Banner attaches to the first row.
          __regimenAlert: idx === 0 && groupSize > 1 && hasAboveStarter,
          __regimenAlertAboveCount: aboveCount,
          __regimenAlertPatientName: list[0]?.patient_name || "this patient",
        });
      });
    }
    return flat;
  }, [orders, statusFilter]);

  // Rows that will actually render in the queue (post auto-approve filter
  // and post-triage sort). Exposed as a memo so the keyboard-shortcut
  // layer can index into the same list the user sees.
  const displayedRows = useMemo(() => {
    let rendered = groupedOrders;
    if (unverifiedOnly) {
      // Keep regimen alert pseudo-rows that belong to a kept group AND
      // any real order row whose patient is none/pending. Verified
      // patients fall away entirely.
      const keptOrderIds = new Set(
        rendered
          .filter((o) => !o.__regimenAlert && (o.id_verification_status === "none" || o.id_verification_status === "pending"))
          .map((o) => o.id),
      );
      rendered = rendered.filter((o) => o.__regimenAlert ? false : keptOrderIds.has(o.id));
    }
    if (triageMode) {
      const priorityIdx = (k) => {
        const idx = triageOrder.indexOf(k);
        return idx === -1 ? 999 : idx;
      };
      rendered = rendered
        .map((o) => ({ o, key: matchedTriageKey(o) }))
        .filter((x) => x.key !== null)
        .sort((a, b) => priorityIdx(a.key) - priorityIdx(b.key))
        .map((x) => x.o);
    }
    return rendered;
  }, [groupedOrders, autoApprovableIds, unverifiedOnly, triageMode, triageOrder, user?._id]);
  const displayedRowIds = useMemo(() => displayedRows.map((o) => o.id), [displayedRows]);

  // Keyboard-shortcut focus index — clamped whenever the row list shrinks
  // (e.g. an order is approved and disappears from pending).
  const [focusedIdx, setFocusedIdx] = useState(null);
  useEffect(() => {
    if (focusedIdx == null) return;
    if (displayedRowIds.length === 0) { setFocusedIdx(null); return; }
    if (focusedIdx >= displayedRowIds.length) setFocusedIdx(displayedRowIds.length - 1);
  }, [displayedRowIds, focusedIdx]);
  // Scroll the focused row into view when it changes via J/K.
  useEffect(() => {
    if (focusedIdx == null) return;
    const id = displayedRowIds[focusedIdx];
    if (!id) return;
    const row = document.querySelector(`[data-testid="order-row-${id}"]`);
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusedIdx, displayedRowIds]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="prescriber-dashboard">
      <div className="flex">
        {/* Sidebar — shown from md: so iPad portrait (820px) gets the
            full nav list. Previously hidden below 1024px, which hid
            Sandbox/Training/Pre-Screened on every tablet. */}
        <aside className="hidden md:flex flex-col w-64 bg-white dark:bg-slate-950 h-screen sticky top-0 border-r border-slate-200 dark:border-slate-800 p-6" data-testid="prescriber-sidebar">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center"><Stethoscope className="w-5 h-5 text-slate-900 dark:text-white" /></div>
            <div>
              <span className="font-bold text-lg text-slate-900 dark:text-white block" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Clinic Portal</span>
            </div>
          </div>
          {/* Clock In/Out */}
          <div className="mb-6 p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Timer className={`w-4 h-4 ${clockedIn ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`} />
              <span className={`text-xs font-medium ${clockedIn ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}>
                {clockedIn ? "Clocked In" : "Clocked Out"}
              </span>
            </div>
            {clockedIn && clockEntry && (
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2">Since {new Date(clockEntry.clock_in).toLocaleTimeString()}</p>
            )}
            <button onClick={handleClockPunch} disabled={clockLoading}
              className={`w-full text-xs font-medium py-2 rounded-lg transition-colors ${clockedIn ? "bg-red-100 dark:bg-red-600/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-600/30" : "bg-emerald-100 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-600/30"}`}
              data-testid="clock-punch-btn">
              {(() => {
                if (clockLoading) return "...";
                return clockedIn ? "Clock Out" : "Clock In";
              })()}
            </button>
          </div>
          <nav className="space-y-1 flex-1 overflow-y-auto">
            {[
              { key: "orders", label: "Order Queue", icon: ClipboardList },
              ...(!isCS ? [{ key: "auto-approve", label: "Pre-Screened", icon: CheckCircle, badgeCount: autoApprovableIds.size, badgeTone: "emerald", testid: "pre-screened-tab-btn" }] : []),
              { key: "patients", label: "Patient Records", icon: Users },
              { key: "tier-review", label: "Tier Review", icon: Repeat, badgeCount: tierReviewCount, testid: "tier-review-tab-btn" },
              { key: "erx", label: "ERX History", icon: FileText },
              { key: "documents", label: "Documents", icon: FolderOpen },
              { key: "myclinics", label: "My Clinics", icon: Building2, testid: "my-clinics-tab-btn" },
              { key: "chat", label: "Live Chat", icon: MessageCircle },
              { key: "delivery-exceptions", label: "Delivery Windows", icon: Plane },
              { key: "activity", label: "My Activity", icon: Clock },
              { key: "myprofile", label: "My Profile", icon: Settings, testid: "my-profile-tab-btn" },
              { key: "training", label: "Training Manual", icon: BookOpen, testid: "training-tab-btn", badge: true },
              { key: "sandbox", label: "Sandbox", icon: FlaskConical, testid: "sandbox-tab-btn" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                data-testid={item.testid}
                className={`group w-full flex items-center gap-3 text-sm rounded-lg px-3 py-2.5 transition-colors ${activeTab === item.key ? "text-purple-700 bg-purple-100 dark:text-white dark:bg-purple-600/20" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-transparent"}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && <UnreadBadge count={trainingUnread} />}
                {typeof item.badgeCount === "number" && item.badgeCount > 0 && (
                  <span
                    className={`text-[10px] font-bold ${
                      item.badgeTone === "emerald"
                        ? "bg-emerald-500 text-white"
                        : "bg-amber-500 text-white"
                    } rounded-full px-1.5 min-w-[18px] text-center`}
                    data-testid={`${item.key}-count-badge`}
                  >
                    {item.badgeCount}
                  </span>
                )}
                <PinTabButton activeKey={item.key} />
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-800">
            <p className="text-sm text-slate-900 dark:text-white font-medium">{user?.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{user?.email}</p>
            <div className="flex items-center gap-2 mb-2">
              <ThemeToggle variant="sidebar" />
              <span className="text-xs text-slate-500 dark:text-slate-400">Theme</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white w-full justify-start mb-1"
              onClick={async () => {
                try {
                  const res = await API.get(`/prescriber/employee-handbook.pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  window.open(url, "_blank", "noopener");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not open handbook");
                }
              }}
              data-testid="employee-handbook-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" /> Employee Handbook
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white w-full justify-start" onClick={handleLogout} data-testid="prescriber-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content - Dark Mode */}
        <main className="flex-1 min-w-0 min-h-screen bg-slate-50 dark:bg-slate-950">
          <div className="md:hidden bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-purple-400" /><span className="font-bold text-slate-900 dark:text-white text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo</span></div>
            <div className="flex items-center gap-2">
              <button onClick={handleClockPunch} disabled={clockLoading} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${clockedIn ? "bg-red-600/20 text-red-400" : "bg-emerald-600/20 text-emerald-400"}`} data-testid="mobile-clock-btn">
                <Timer className="w-3 h-3 inline mr-1" />{clockedIn ? "Out" : "In"}
              </button>
              <TrainingHelpButton onOpenFullManual={() => setActiveTab("training")} variant="header" />
              <ThemeToggle variant="sidebar" />
              <Button variant="ghost" size="sm" className="text-slate-400 dark:text-slate-500" onClick={handleLogout}><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="md:hidden flex gap-2 p-3 overflow-x-auto bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
            {[
              { key: "orders", label: "Orders", icon: ClipboardList },
              ...(!isCS ? [{ key: "auto-approve", label: "Pre-Screened", icon: CheckCircle }] : []),
              { key: "patients", label: "Patients", icon: Users },
              { key: "tier-review", label: "Tier Review", icon: Repeat },
              { key: "erx", label: "ERX", icon: FileText },
              { key: "documents", label: "Docs", icon: FolderOpen },
              { key: "myclinics", label: "My Clinics", icon: Building2 },
              { key: "chat", label: "Live Chat", icon: MessageCircle },
              { key: "delivery-exceptions", label: "Delivery Windows", icon: Plane },
              { key: "activity", label: "Activity", icon: Clock },
              { key: "myprofile", label: "My Profile", icon: Settings },
              { key: "training", label: "Training", icon: BookOpen },
              { key: "sandbox", label: "Sandbox", icon: FlaskConical },
            ].map((t) => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`mobile-tab-${t.key}`} className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-full whitespace-nowrap flex-shrink-0 ${activeTab === t.key ? "bg-purple-600 text-slate-900 dark:text-white" : "bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-500"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            <HIPAABanner />
            <LabFloStatusBanner />
            {/* ===== ORDERS TAB ===== */}
            {activeTab === "orders" && (
              <div>
                {user?.role === "prescriber" && (
                  <PrescriberProfileWarning onGoToProfile={() => setActiveTab("myprofile")} />
                )}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Order Queue</h1>
                    </div>
                    <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Review and process medication orders — rows marked <span className="inline-flex items-center gap-1 text-[10px] bg-purple-500/20 text-purple-300 border border-purple-700/40 px-1.5 py-0.5 rounded-full font-semibold"><Timer className="w-3 h-3" />PS</span> have passed automated pre-screening (clinician review still required)</p>
                    {/* AI-screening KPI banner — three at-a-glance stats that
                        sell the automation story for investors looking at the
                        screen for the first time. Reads as headline numbers
                        rather than a debug-style chip. Hidden when the queue
                        is empty so a fresh inbox doesn't show "0 · 0% · 0". */}
                    {stats.pending > 0 && (
                      <div
                        data-testid="ps-kpi-strip"
                        className="mt-3 inline-flex items-stretch gap-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40 overflow-hidden shadow-sm"
                      >
                        <KpiStat
                          testid="ps-kpi-screened"
                          value={autoApprovableIds.size}
                          label="pre-screened"
                          accent="emerald"
                        />
                        <KpiStat
                          testid="ps-kpi-rate"
                          value={`${Math.round((autoApprovableIds.size / stats.pending) * 100)}%`}
                          label="automation rate"
                          accent="purple"
                        />
                        <KpiStat
                          testid="ps-kpi-review"
                          value={Math.max(stats.pending - autoApprovableIds.size, 0)}
                          label="require clinician review"
                          accent="amber"
                          last
                        />
                        <HelpLink
                          sectionId="rx-auto-approve"
                          title="Training: Pre-Screened (PS) Badge — What It Means"
                          size={14}
                          className="self-center px-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label
                      className={`flex items-center gap-2 text-xs cursor-pointer select-none px-2.5 py-1 rounded-full border-2 transition-colors ${
                        aboveStarterOnly
                          ? "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                          : "border-rose-200 text-rose-700 hover:border-rose-300 dark:border-rose-900/60 dark:text-rose-300/80"
                      }`}
                      data-testid="above-starter-filter-toggle"
                      title="Show only patient-requested above-starter orders — these patients acknowledged the $25-on-denial warning at order time."
                    >
                      <input
                        type="checkbox"
                        checked={aboveStarterOnly}
                        onChange={(e) => setAboveStarterOnly(e.target.checked)}
                        className="hidden"
                      />
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span className="font-semibold">Above-starter requests</span>
                    </label>
                    {/* Unverified-only filter — pairs with the "No ID" / "ID
                        review" badges in each row. Lets the clinician
                        triage which patients need an ID nudge in 2-min
                        batches. Counts shown live (n) so it doesn't
                        look like an empty filter if zero match. */}
                    {(() => {
                      const unvCount = groupedOrders.filter(
                        (o) => !o.__regimenAlert && (o.id_verification_status === "none" || o.id_verification_status === "pending"),
                      ).length;
                      return (
                        <label
                          className={`flex items-center gap-2 text-xs cursor-pointer select-none px-2.5 py-1 rounded-full border-2 transition-colors ${
                            unverifiedOnly
                              ? "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                              : "border-rose-200 text-rose-700 hover:border-rose-300 dark:border-rose-900/60 dark:text-rose-300/80"
                          }`}
                          data-testid="unverified-filter-toggle"
                          title="Show only rows whose patient hasn't completed Identity Verification (No ID + ID review states)."
                        >
                          <input
                            type="checkbox"
                            checked={unverifiedOnly}
                            onChange={(e) => setUnverifiedOnly(e.target.checked)}
                            className="hidden"
                          />
                          <ShieldAlert className="w-3.5 h-3.5" />
                          <span className="font-semibold">Unverified only{unvCount ? ` (${unvCount})` : ""}</span>
                        </label>
                      );
                    })()}
                    <Button variant="outline" size="sm" className="rounded-lg border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-800" onClick={loadData} data-testid="refresh-orders-btn">
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    {/* Demo-state reset — visible to admin + prescriber. Re-seeds
                        the 10 *@investor-demo.glpflo.test orders so a pitch can
                        be re-run cleanly without an SSH session. Hidden for
                        admins without the `reset_demo_state` capability. */}
                    {canResetDemo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetDemoState}
                        disabled={resettingDemo}
                        title="Re-seed the investor-demo orders (does not touch real patient data)"
                        className="rounded-lg border-purple-300 dark:border-purple-700/60 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                        data-testid="reset-demo-state-btn"
                      >
                        {resettingDemo ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-1" />
                        )}
                        {resettingDemo ? "Resetting…" : "Reset demo"}
                      </Button>
                    )}
                  </div>
                </div>
                {/* My commission preview — pay-per-order projection */}
                <div className="mb-6">
                  <MyCommissionCard />
                </div>
                {/* Queue scope tabs — "All Orders" (every incoming order;
                    only MDs can claim/approve from here) vs "My Queue"
                    (sticky-assigned-to-me + unassigned orders in tiers
                    I'm authorized to fill). Non-MDs default to My Queue. */}
                <div className="mb-5 inline-flex bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-1" data-testid="queue-scope-tabs">
                  <button
                    type="button"
                    onClick={() => setQueueScope("all")}
                    data-testid="queue-scope-all"
                    title={isMd ? "Every incoming order — you can claim any of them" : "Every incoming order — only MDs may claim from this queue. PA/NPs should use 'My Queue'."}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      queueScope === "all"
                        ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    }`}
                  >
                    All Orders
                    {!isMd && <span className="ml-1.5 text-[10px] text-amber-600 dark:text-amber-400">(view only)</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setQueueScope("mine")}
                    data-testid="queue-scope-mine"
                    title="Orders sticky-assigned to me (refill continuity-of-care) + unassigned orders in tiers I'm authorized to fill."
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      queueScope === "mine"
                        ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    }`}
                  >
                    My Queue
                  </button>
                </div>

                {/* Triage mode toggle — focuses the queue on rows that
                    need attention right now, sorted by the prescriber's
                    saved priority order. Persists in localStorage. */}
                <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid="triage-toolbar">
                  <button
                    type="button"
                    onClick={() => setTriageMode((v) => !v)}
                    data-testid="triage-mode-toggle"
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                      triageMode
                        ? "bg-amber-500 border-amber-600 text-white shadow-sm"
                        : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-amber-400"
                    }`}
                    title="Show only orders that need attention right now (reassigned, warned, above-starter, patient notes)"
                  >
                    <span aria-hidden>⚡</span> Triage mode
                    {triageMode && <span className="ml-1 opacity-90">on</span>}
                  </button>
                  {triageMode && (
                    <button
                      type="button"
                      onClick={() => setShowTriageEditor((v) => !v)}
                      data-testid="triage-edit-order-btn"
                      className="text-xs text-slate-600 dark:text-slate-300 hover:text-amber-700 dark:hover:text-amber-400 underline-offset-2 hover:underline"
                    >
                      {showTriageEditor ? "Hide order editor" : "Customize order"}
                    </button>
                  )}
                </div>

                {/* Triage filter-order editor — up/down to reorder. Saves
                    on each change so close-without-save is impossible. */}
                {triageMode && showTriageEditor && (
                  <div className="mb-5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4" data-testid="triage-editor">
                    <p className="text-xs text-amber-800 dark:text-amber-200 mb-3 font-semibold uppercase tracking-wide">Your triage priority order (top = highest)</p>
                    <ul className="space-y-1.5">
                      {triageOrder.map((key, idx) => {
                        const labels = {
                          sla_reassigned: "⏱  Reassigned from another prescriber",
                          halfway_warned: "⏰  Halfway-warned (auto-reassign soon)",
                          above_starter: "🛡  Above-starter dose request",
                          patient_note: "💬  Unacknowledged patient note",
                          pending_review: "📋  Standard pending review",
                        };
                        return (
                          <li key={key} className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2" data-testid={`triage-row-${key}`}>
                            <span className="text-[10px] font-mono font-bold text-slate-500 w-5">{idx + 1}.</span>
                            <span className="flex-1 text-sm text-slate-800 dark:text-slate-100">{labels[key] || key}</span>
                            <button
                              type="button"
                              onClick={() => moveTriageFilter(idx, -1)}
                              disabled={idx === 0}
                              data-testid={`triage-up-${key}`}
                              className="px-1.5 py-0.5 rounded text-xs text-slate-500 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >▲</button>
                            <button
                              type="button"
                              onClick={() => moveTriageFilter(idx, 1)}
                              disabled={idx === triageOrder.length - 1}
                              data-testid={`triage-down-${key}`}
                              className="px-1.5 py-0.5 rounded text-xs text-slate-500 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >▼</button>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-[10.5px] text-amber-700 dark:text-amber-400 mt-2 italic">Saved automatically. Each row uses the priority order to break ties between filters.</p>
                  </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6" data-testid="prescriber-stats">
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-4 cursor-pointer hover:border-amber-600/50 transition-all" onClick={() => setStatusFilter("pending_review")}>
                    <Clock className="w-5 h-5 text-amber-500 mb-2" /><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.pending}</p><p className="text-xs text-slate-400 dark:text-slate-500">Pending</p>
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-4 cursor-pointer hover:border-emerald-600/50 transition-all" onClick={() => setStatusFilter("approved")}>
                    <CheckCircle className="w-5 h-5 text-emerald-500 mb-2" /><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.approved}</p><p className="text-xs text-slate-400 dark:text-slate-500">Approved</p>
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-4 cursor-pointer hover:shadow-md transition-all" onClick={() => setStatusFilter("denied")}>
                    <XCircle className="w-5 h-5 text-red-500 mb-2" /><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.denied}</p><p className="text-xs text-slate-500 dark:text-slate-400">Denied</p>
                  </div>
                </div>
                {/* Orders Table */}
                {loading ? <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading...</div> : orders.length === 0 ? (
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-12 text-center"><ClipboardList className="w-10 h-10 text-slate-600 dark:text-slate-300 mx-auto mb-3" /><p className="text-slate-500 dark:text-slate-400">No {statusFilter.replace("_", " ")} orders</p></div>
                ) : (
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 overflow-hidden" data-testid="orders-table">
                    <table className="w-full text-sm">
                      <thead className="bg-white dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700">
                        <tr>
                          <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Patient</th>
                          <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Medication</th>
                          <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium hidden md:table-cell">Type</th>
                          <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium hidden md:table-cell">Payment</th>
                          <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Status</th>
                          <th className="text-right px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                        {displayedRows.map((order, rowIdx) => (
                          <Fragment key={order.id}>
                            {order.__regimenAlert && (
                              <tr
                                key={`alert-${order.__groupKey}`}
                                className="bg-amber-50 dark:bg-amber-950/30 border-t-2 border-amber-300 dark:border-amber-700"
                                data-testid={`regimen-alert-${order.__groupKey}`}
                              >
                                <td colSpan={6} className="px-4 py-2.5">
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-900 dark:text-amber-100">
                                    <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-300 shrink-0" />
                                    <span>
                                      <span className="font-semibold">Heads up — regimen-level review:</span>{" "}
                                      {order.__regimenAlertPatientName} has {order.__groupSize} pending requests
                                      {order.__regimenAlertAboveCount > 0 && (
                                        <>
                                          {" "}AND {order.__regimenAlertAboveCount === 1 ? "is" : "has"}{" "}
                                          <span className="font-semibold underline decoration-amber-400">
                                            {order.__regimenAlertAboveCount === 1
                                              ? "requesting an above-starter dose on one of them"
                                              : `requesting above-starter doses on ${order.__regimenAlertAboveCount}`}
                                          </span>
                                        </>
                                      )}
                                      . Review the full regimen before approving any single order.
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          <tr
                            key={order.id}
                            className={`hover:bg-slate-800/50 transition-colors ${
                              order.__groupSize > 1
                                ? "bg-purple-50/40 dark:bg-purple-950/10 border-l-4 border-l-purple-400 dark:border-l-purple-600"
                                : ""
                            } ${order.__groupFirst && !order.__regimenAlert ? "!border-t-2 !border-t-purple-300 dark:!border-t-purple-700/60" : ""} ${
                              focusedIdx === rowIdx ? "ring-2 ring-amber-400 ring-inset dark:ring-amber-500" : ""
                            }`}
                            data-testid={`order-row-${order.id}`}
                            data-focused={focusedIdx === rowIdx ? "1" : "0"}
                            data-group-key={order.__groupKey || ""}
                            data-group-size={order.__groupSize || 1}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => { setPatientDetailTab("wellness"); setPatientDetailId(order.patient_id); }}
                                  className="font-medium text-purple-700 dark:text-purple-300 hover:underline text-left"
                                  title="View patient profile, call, text, add notes"
                                  data-testid={`open-patient-${order.patient_id}-${order.id}`}
                                >
                                  {order.patient_name}
                                </button>
                                {order.__groupSize > 1 && (
                                  <span
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-purple-400 bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-600"
                                    title={`This patient has ${order.__groupSize} pending medication requests in this queue — approve the full regimen together so none are missed.`}
                                    data-testid={`regimen-badge-${order.id}`}
                                  >
                                    Regimen · {order.__groupIndex + 1}/{order.__groupSize}
                                  </span>
                                )}
                                <ClickToCallButton
                                  patientId={order.patient_id}
                                  patientLabel={order.patient_name}
                                  compact
                                />
                                {autoApprovableIds.has(order.id) && (
                                  <span className="inline-flex items-center gap-1 text-[10px] bg-purple-500/20 text-purple-300 border border-purple-700/40 px-2 py-0.5 rounded-full font-semibold" title="Pre-screened: passed automated allergy, drug, and medical-history checks — clinician review still required before approval" data-testid={`aa-badge-${order.id}`}>
                                    <Timer className="w-3 h-3" /> PS
                                  </span>
                                )}
                                {/* Identity-verification badge — surfaces the
                                    patient's ID-verify state right in the queue
                                    so clinicians know whether to ask for ID or
                                    a re-record BEFORE opening the patient
                                    record. Only shown on pending/none rows so
                                    verified patients (the common case) stay
                                    visually quiet. */}
                                {order.id_verification_status === "none" && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setPatientDetailTab("id-verify"); setPatientDetailId(order.patient_id); }}
                                    className="inline-flex items-center gap-1 text-[10px] bg-rose-500/15 hover:bg-rose-500/25 text-rose-700 dark:text-rose-300 border border-rose-400 dark:border-rose-700/50 px-2 py-0.5 rounded-full font-semibold cursor-pointer transition-colors"
                                    title="Patient has not uploaded any ID or attestation video yet — click to open the identity review panel."
                                    data-testid={`idverify-badge-none-${order.id}`}
                                  >
                                    <ShieldAlert className="w-3 h-3" /> No ID
                                  </button>
                                )}
                                {order.id_verification_status === "pending" && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setPatientDetailTab("id-verify"); setPatientDetailId(order.patient_id); }}
                                    className="inline-flex items-center gap-1 text-[10px] bg-amber-500/15 hover:bg-amber-500/25 text-amber-700 dark:text-amber-300 border border-amber-400 dark:border-amber-700/50 px-2 py-0.5 rounded-full font-semibold cursor-pointer transition-colors"
                                    title="Identity documents uploaded but not yet verified — click to open the review panel."
                                    data-testid={`idverify-badge-pending-${order.id}`}
                                  >
                                    <ShieldAlert className="w-3 h-3" /> ID review
                                  </button>
                                )}
                                {/* Nudge button — only renders for "No ID"
                                    state (the only state where a reminder is
                                    actionable for the patient). After click,
                                    the row gets a "Nudged Xm ago" timestamp
                                    and the button is disabled for 6 hours via
                                    the backend cooldown. */}
                                {order.id_verification_status === "none" && (
                                  <IdNudgeButton
                                    orderId={order.id}
                                    patientId={order.patient_id}
                                    lastNudgedAt={order.id_verification_nudged_at}
                                    onSent={loadData}
                                  />
                                )}
                              </div>
                              <p className="text-xs text-slate-400 dark:text-slate-500">{order.patient_email}</p>
                              {order.patient_display_id && (
                                <p className="text-[11px] text-purple-500 dark:text-purple-400 font-mono font-semibold" data-testid={`row-patient-id-${order.id}`}>
                                  {order.patient_display_id}
                                </p>
                              )}
                              {order.patient_note && !order.patient_note_acknowledged && (
                                <span className="inline-flex items-center gap-1 text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full mt-1 font-medium" data-testid={`pt-note-badge-${order.id}`}>
                                  <AlertCircle className="w-3 h-3" /> Patient note
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                              <div className="flex flex-col gap-0.5">
                                <span>{order.medication_name}</span>
                                {order.requested_strength && (
                                  <span
                                    className="inline-flex items-center gap-1 self-start text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-purple-300 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-700"
                                    data-testid={`requested-strength-${order.id}`}
                                    title={
                                      order.requested_strength_source === "previous_rx"
                                        ? "Patient is currently on this strength (last ERX)"
                                        : order.requested_strength_source === "previous_request"
                                          ? "Same as patient's prior request"
                                          : "Default starting dose for new patients"
                                    }
                                  >
                                    Requested: {order.requested_strength}
                                    {order.requested_strength_source === "starter_default" && (
                                      <span className="font-normal normal-case opacity-70 ml-1">(start)</span>
                                    )}
                                  </span>
                                )}
                                {order.dose_request_above_starter && (
                                  <span
                                    className="inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-rose-400 bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700/60 animate-pulse"
                                    data-testid={`above-starter-flag-${order.id}`}
                                    title={`Patient requested ${order.requested_strength} above safe starter (${order.starter_strength || "starter"}). They acknowledged $25 will be retained on denial.`}
                                  >
                                    <ShieldAlert className="w-3 h-3" />
                                    Above starter
                                    {order.dose_request_acknowledged ? <span className="opacity-70">· pt ack&rsquo;d</span> : null}
                                  </span>
                                )}
                                {/* Live-lock pill — surfaces "🔒 In review by Dr. X"
                                    before the prescriber wastes a click. Pulses
                                    when someone ELSE holds the lock; goes solid
                                    blue when I'm the holder so I can spot my own
                                    open modals. Auto-refreshes via the 30s poll. */}
                                {activeLocks[order.id] && (
                                  <span
                                    className={`inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                                      activeLocks[order.id].is_mine
                                        ? "border-sky-400 bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-700/60"
                                        : "border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700/60 animate-pulse"
                                    }`}
                                    data-testid={`lock-pill-${order.id}`}
                                    title={
                                      activeLocks[order.id].is_mine
                                        ? `You are reviewing this order — lock auto-releases after 5 minutes idle.`
                                        : `${activeLocks[order.id].holder_name || "Another prescriber"} is reviewing this order. Lock expires ${new Date(activeLocks[order.id].expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`
                                    }
                                  >
                                    <Lock className="w-3 h-3" />
                                    {activeLocks[order.id].is_mine
                                      ? "You"
                                      : (activeLocks[order.id].holder_name || "In review")}
                                  </span>
                                )}
                                {/* SLA-reassignment badge — surfaces orders that
                                    were yanked off another prescriber by the
                                    hourly sweep. Helps the receiving prescriber
                                    triage faster ("this one's been waiting"). */}
                                {order.assignment_source === "sla_reassigned" && (
                                  <span
                                    className="inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-orange-400 bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-700/60"
                                    data-testid={`sla-badge-${order.id}`}
                                    title={`Re-routed from ${order.sla_reassigned_from_name || "previous prescriber"} after sitting ${Math.round(order.sla_reassigned_age_hours || 0)}h — please review promptly.`}
                                  >
                                    ⏱ Reassigned
                                    {order.sla_reassigned_age_hours ? ` · ${Math.round(order.sla_reassigned_age_hours)}h idle` : ""}
                                  </span>
                                )}
                                {/* SLA halfway-warning badge — shown on orders
                                    still assigned to ME that have been warned
                                    by the hourly sweep. Gentle prod to act
                                    before the full-threshold yank. */}
                                {order.sla_warning_sent_at && order.prescriber_id === user?._id && order.assignment_source !== "sla_reassigned" && (
                                  <span
                                    className="inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700/60 animate-pulse"
                                    data-testid={`sla-warning-badge-${order.id}`}
                                    title={`This order has been waiting ${Math.round(order.sla_warning_age_hours || 0)}h on your queue and will auto-reassign if not acted on. Click to review.`}
                                  >
                                    ⏰ Act soon · {Math.round(order.sla_warning_age_hours || 0)}h
                                  </span>
                                )}
                                {/* Doc-completeness chip — triage signal for prescriber. */}
                                {(() => {
                                  if (!order.dose_request_above_starter) return null;
                                  const doc = order.above_starter_documentation;
                                  if (!doc) {
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600"
                                        data-testid={`doc-score-${order.id}`}
                                        title="No documentation captured (legacy order)"
                                      >
                                        Docs: 0/4
                                      </span>
                                    );
                                  }
                                  if (typeof doc === "string") {
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600"
                                        data-testid={`doc-score-${order.id}`}
                                        title="Legacy free-text documentation"
                                      >
                                        Docs: legacy
                                      </span>
                                    );
                                  }
                                  const filled = ["pharmacy", "prescriber", "dose", "duration"].filter(
                                    (k) => typeof doc[k] === "string" && doc[k].trim().length >= 3
                                  ).length;
                                  const full = filled === 4;
                                  const partial = filled >= 2 && filled < 4;
                                  const cls = full
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700"
                                    : partial
                                      ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700"
                                      : "border-rose-300 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-700";
                                  return (
                                    <span
                                      className={`inline-flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}
                                      data-testid={`doc-score-${order.id}`}
                                      title={`Documentation completeness: ${filled} of 4 required fields filled (≥3 chars each)`}
                                    >
                                      Docs: {filled}/4 {full ? "✓" : partial ? "⚠" : "✗"}
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden md:table-cell">{order.medication_type}</td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className={`text-xs px-2 py-1 rounded-full ${order.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300"}`}>
                                {order.payment_status || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3"><div className="flex flex-wrap items-center gap-1"><span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border ${STATUS_COLORS[order.status] || ""}`}>{order.status.replace(/_/g, " ")}</span>{order.erx_id && <LabFloStatusChip erxId={order.erx_id} erxStatus={order.erx_status} compact testIdSuffix={order.id} />}</div></td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" className="text-slate-600 dark:text-slate-300 hover:text-white" onClick={() => viewOrderDetails(order)} data-testid={`view-order-${order.id}`}><Eye className="w-4 h-4" /></Button>
                                {order.status === "approved" && order.erx_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 dark:text-purple-300 dark:hover:text-white dark:hover:bg-purple-900/30"
                                    onClick={() => openErxPdf(order.erx_id)}
                                    title="View eRx PDF"
                                    data-testid={`view-erx-pdf-${order.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                {order.status === "approved" && order.erx_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:text-white dark:hover:bg-emerald-900/30"
                                    onClick={() => resendToPharmacy(order)}
                                    disabled={resendingId === order.id}
                                    title="Resend to Polomar (LifeFile)"
                                    data-testid={`resend-pharmacy-${order.id}`}
                                  >
                                    {resendingId === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                  </Button>
                                )}
                                {order.status === "pending_review" && !isCS && (
                                  <>
                                    {/* Claim button — shown only for unassigned
                                        orders. MDs can claim any tier; non-MDs
                                        only their allowed_tiers (server enforces). */}
                                    {!order.prescriber_id && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-purple-400 text-purple-700 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-300 dark:hover:bg-purple-950/40 rounded-lg"
                                        onClick={() => claimOrder(order)}
                                        data-testid={`claim-order-${order.id}`}
                                        title="Claim this order so it's assigned to you before you write the eRx"
                                      >
                                        <Hand className="w-3 h-3 mr-1" /> Claim
                                      </Button>
                                    )}
                                    {order.dose_request_above_starter && order.starter_strength && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-rose-400 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950/40 rounded-lg"
                                        onClick={() => openRecommendStarter(order)}
                                        title={`Pre-fill ERX with starter dose ${order.starter_strength}`}
                                        data-testid={`recommend-starter-${order.id}`}
                                      >
                                        <ShieldAlert className="w-3 h-3 mr-1" /> Starter instead
                                      </Button>
                                    )}
                                    {canPrescribe && (
                                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white rounded-lg" onClick={() => openActionModal(order)} data-testid={`approve-order-${order.id}`}>
                                        <Pill className="w-3 h-3 mr-1" /> Write ERX
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ===== PATIENTS TAB ===== */}
            {activeTab === "patients" && (
              <div>
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Patient Records</h1>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
                    <Input
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      placeholder="Search last name, first name, or DOB (MM/DD/YYYY)"
                      className="pl-9 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
                      data-testid="patient-search-input"
                    />
                  </div>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 overflow-hidden" data-testid="patients-table">
                  <table className="w-full text-sm">
                    <thead className="bg-white dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700">
                      <tr>
                        <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Name</th>
                        <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium">Email</th>
                        <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium hidden md:table-cell">DOB</th>
                        <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium hidden md:table-cell">Gender</th>
                        <th className="text-left px-4 py-3 text-slate-400 dark:text-slate-500 font-medium hidden md:table-cell">Phone</th>
                        <th className="text-right px-4 py-3 text-slate-400 dark:text-slate-500 font-medium w-16">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPatients.map((p, idx) => (
                        <tr
                          key={p.id}
                          onClick={() => viewPatientDetail(p.id)}
                          className={`cursor-pointer transition-all border-b border-slate-200 dark:border-slate-800 ${idx % 2 === 0 ? "bg-white dark:bg-slate-800/60" : "bg-slate-50 dark:bg-slate-800/20"} hover:bg-purple-50 dark:hover:bg-purple-600/10 hover:shadow-[inset_0_0_0_1px_rgba(124,58,237,0.4)]`}
                          data-testid={`patient-row-${p.id}`}
                        >
                          <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{p.name}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.email}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden md:table-cell">{p.date_of_birth || "-"}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden md:table-cell capitalize">{p.gender || "-"}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 hidden md:table-cell">{p.phone || "-"}</td>
                          <td className="px-4 py-3 text-right">
                            <Eye className="w-4 h-4 text-purple-400 inline-block" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredPatients.length === 0 && (
                    <div className="p-8 text-center text-slate-400 dark:text-slate-500">
                      {patientSearch ? "No patients match your search" : "No patients yet"}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== ERX TAB ===== */}
            {activeTab === "erx" && <ERXHistoryTab />}
            {activeTab === "tier-review" && <TierReviewTab />}

            {/* ===== DOCUMENTS TAB ===== */}
            {activeTab === "documents" && <PrescriberDocumentsTab patients={patients} />}

            {activeTab === "myclinics" && <MyClinicsTab />}

            {/* ===== CHAT TAB ===== */}
            {activeTab === "chat" && <StaffChatTab />}
            {activeTab === "delivery-exceptions" && <DeliveryExceptionsAdminTab />}

            {/* ===== AUTO-APPROVE QUEUE ===== */}
            {activeTab === "auto-approve" && <AutoApproveTab loadData={loadData} patients={patients} onOpenOrderAction={openActionModal} />}

            {/* ===== ACTIVITY TAB ===== */}
            {activeTab === "activity" && <PrescriberActivityTab />}

            {/* ===== MY PROFILE TAB ===== */}
            {activeTab === "myprofile" && <StaffProfileTab />}

            {/* ===== TRAINING MANUAL TAB ===== */}
            {activeTab === "training" && <StaffTrainingManual />}
            {activeTab === "sandbox" && <SandboxTab onOpenOrderAction={openActionModal} />}
          </div>
        </main>
      </div>

      {/* Install Portal App */}
      {/* InstallAppPrompt popup removed — inline card lives on /cliniclogin login page */}

      {/* ===== ORDER DETAIL MODAL ===== */}
      <Dialog open={!!selectedOrder} onOpenChange={() => { setSelectedOrder(null); setQuestionnaire(null); setOrderPatient(null); setOrderReceipt(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Order Details</DialogTitle></DialogHeader>
          {selectedOrder && (
            <>
              <div className="mb-4">
                <DDIWarningBanner orderId={selectedOrder.id} />
              </div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Order ID · use in LifeFile / Polomar tab</p>
                  <span
                    onClick={() => { navigator.clipboard?.writeText(selectedOrder.id); toast.success("Order ID copied"); }}
                    role="button"
                    tabIndex={0}
                    className="font-mono text-sm text-slate-800 dark:text-slate-100 hover:underline inline-flex items-center gap-1.5 cursor-pointer"
                    title="Click to copy"
                    data-testid="order-detail-id"
                  >
                    {selectedOrder.id} <Copy className="w-3 h-3 opacity-60" />
                  </span>
                </div>
                {selectedOrder.status === "approved" && selectedOrder.erx_id && (
                  <Button
                    size="sm"
                    onClick={() => resendToPharmacy(selectedOrder)}
                    disabled={resendingId === selectedOrder.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shrink-0"
                    data-testid={`resend-pharmacy-detail-${selectedOrder.id}`}
                  >
                    {resendingId === selectedOrder.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    {resendingId === selectedOrder.id ? "Sending…" : "Resend to Polomar"}
                  </Button>
                )}
              </div>
              <Tabs defaultValue="order" className="w-full" data-testid="order-detail-tabs">
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="order">Order Info</TabsTrigger>
                <TabsTrigger value="patient">Patient Demographics</TabsTrigger>
                <TabsTrigger value="questionnaire">Questionnaire</TabsTrigger>
              </TabsList>
              <TabsContent value="order">
                {selectedOrder.requested_strength && (
                  <div
                    className="mb-4 rounded-lg border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 dark:border-purple-700 p-4 flex items-start gap-3"
                    data-testid="order-detail-requested-strength"
                  >
                    <Pill className="w-5 h-5 text-purple-600 dark:text-purple-300 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300">
                        Patient requested
                      </p>
                      <p className="text-base font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
                        {selectedOrder.requested_strength_label || `${selectedOrder.medication_name} ${selectedOrder.requested_strength}`}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        {selectedOrder.requested_strength_source === "starter_default" && (
                          <>Default starting dose for new patients. Adjust below if clinically warranted.</>
                        )}
                        {selectedOrder.requested_strength_source === "previous_rx" && (
                          <>Patient is currently on this strength (carried from their last ERX). Step up only if titration is clinically appropriate.</>
                        )}
                        {selectedOrder.requested_strength_source === "previous_request" && (
                          <>Same strength as the patient&apos;s prior order on file.</>
                        )}
                        {(!selectedOrder.requested_strength_source || selectedOrder.requested_strength_source === "unknown") && (
                          <>Strength inferred from medication catalog.</>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Patient</p><p className="font-medium">{selectedOrder.patient_name}</p></div>
                  {selectedOrder.patient_display_id && (
                    <div><p className="text-xs text-slate-500 dark:text-slate-400">Patient ID</p><p className="font-mono font-semibold text-purple-700 dark:text-purple-300" data-testid="order-detail-patient-id">{selectedOrder.patient_display_id}</p></div>
                  )}
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Email</p><p className="font-medium">{selectedOrder.patient_email}</p></div>
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Medication</p><p className="font-medium">{selectedOrder.medication_name}</p></div>
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Type</p><p className="font-medium">{selectedOrder.medication_type}</p></div>
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Status</p><p className="font-medium capitalize">{selectedOrder.status.replace(/_/g, " ")}</p></div>
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Payment</p><p className="font-medium capitalize">{selectedOrder.payment_status || "N/A"}</p></div>
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">Date</p><p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p></div>
                  {selectedOrder.erx_id && <div><p className="text-xs text-slate-500 dark:text-slate-400">ERX</p><button onClick={() => openErxPdf(selectedOrder.erx_id)} className="font-medium text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1" data-testid={`modal-view-erx-pdf-${selectedOrder.id}`}>{selectedOrder.erx_id} <ExternalLink className="w-3 h-3" /></button></div>}
                  {selectedOrder.tracking_number && <div><p className="text-xs text-slate-500 dark:text-slate-400">Tracking</p><p className="font-medium font-mono">{selectedOrder.tracking_number}</p></div>}
                </div>
                {/* LabFlo sync status — only shown once an ERX has been issued */}
                {selectedOrder.erx_id && selectedOrder.erx_status && (
                  <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="erx-labflo-status-row">
                    {selectedOrder.erx_status.labflo_synced ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300"
                        data-testid="erx-sent-to-labflo-badge"
                        title={selectedOrder.erx_status.labflo_rx_number ? `Polomar Rx ${selectedOrder.erx_status.labflo_rx_number}` : "Transmitted to Polomar"}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Sent to Polomar
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300"
                        data-testid="erx-not-synced-badge"
                        title="ERX saved locally but not yet transmitted to Polomar"
                      >
                        <AlertCircle className="w-3 h-3" /> Not sent to Polomar
                      </span>
                    )}
                    {selectedOrder.erx_status.pdf_attached && (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-300"
                        data-testid="erx-pdf-attached-badge"
                        title={
                          selectedOrder.erx_status.pdf_size_bytes
                            ? `Signed PDF delivered (${(selectedOrder.erx_status.pdf_size_bytes / 1024).toFixed(1)} KB)`
                            : "Signed PDF delivered"
                        }
                      >
                        <FileCheck className="w-3 h-3" /> PDF attached
                      </span>
                    )}
                    {selectedOrder.erx_status.labflo_rx_number && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-mono" data-testid="erx-labflo-rx-number">
                        Rx #{selectedOrder.erx_status.labflo_rx_number}
                      </span>
                    )}
                  </div>
                )}
                {/* Receipt download — staff can re-pull the PDF for support */}
                {orderReceipt && (                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60 bg-purple-50 dark:bg-purple-950/30 -mx-6 px-6 py-4 flex items-center gap-3" data-testid="prescriber-receipt-block">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-900 border border-purple-100 dark:border-purple-800 flex items-center justify-center shrink-0">
                      <Receipt className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Payment receipt available</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Receipt <span className="font-mono">{orderReceipt.receipt_id}</span> · {orderReceipt.tier_label} · ${orderReceipt.amount.toFixed(2)} {orderReceipt.currency}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full shrink-0"
                      onClick={downloadOrderReceipt}
                      disabled={downloadingReceipt}
                      data-testid="prescriber-download-receipt-btn"
                    >
                      {downloadingReceipt ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                      Download PDF
                    </Button>
                  </div>
                )}
                {/* Internal notes — order-specific staff annotations */}
                <OrderNotesPanel orderId={selectedOrder.id} className="mt-4" />
                {/* Tracking Update for approved orders */}
                {selectedOrder.status === "approved" && (
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60" data-testid="tracking-update">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Update Shipping Status</p>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-slate-900 dark:text-white rounded-lg" onClick={async () => {
                        const tracking = prompt("Enter tracking number (optional):");
                        try {
                          await API.put(`/orders/${selectedOrder.id}/tracking`, { status: "shipped", tracking_number: tracking || null, tracking_carrier: "USPS" });
                          toast.success("Order marked as shipped");
                          loadData(); setSelectedOrder(null);
                        } catch { toast.error("Failed to update"); }
                      }} data-testid="mark-shipped-btn">
                        Mark as Shipped
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg text-green-600 border-green-200" onClick={async () => {
                        try {
                          await API.put(`/orders/${selectedOrder.id}/tracking`, { status: "delivered" });
                          toast.success("Order marked as delivered");
                          loadData(); setSelectedOrder(null);
                        } catch { toast.error("Failed to update"); }
                      }} data-testid="mark-delivered-btn">
                        Mark as Delivered
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="patient">
                {orderPatient ? (
                  <div className="space-y-4" data-testid="patient-demographics">
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">Name</p><p className="font-medium">{orderPatient.name || "-"}</p></div>
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">Email</p><p className="font-medium">{orderPatient.email || "-"}</p></div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Phone</p>
                        {orderPatient.phone ? (
                          <div className="flex items-center gap-3 flex-wrap">
                            <ClickToCallPhone phone={orderPatient.phone} patientId={orderPatient.id || orderPatient._id} patientName={orderPatient.name} />
                            <ClickToTextPhone phone={orderPatient.phone} patientId={orderPatient.id || orderPatient._id} patientName={orderPatient.name} />
                          </div>
                        ) : (
                          <p className="font-medium">-</p>
                        )}
                      </div>
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">DOB</p><p className="font-medium">{orderPatient.date_of_birth || "-"}</p></div>
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">Gender</p><p className="font-medium">{orderPatient.gender || "-"}</p></div>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Address</p>
                      <p className="font-medium">{[orderPatient.address_street, orderPatient.address_line2, orderPatient.address_city, orderPatient.address_state, orderPatient.address_zip].filter(Boolean).join(", ") || "-"}</p>
                      {orderPatient.address_gate_code && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Gate/Access Code: <span className="font-medium text-slate-600 dark:text-slate-300">{orderPatient.address_gate_code}</span></p>}
                    </div>
                    <div className="grid grid-cols-3 gap-4 border-t pt-4">
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">Emergency Contact</p><p className="font-medium">{orderPatient.emergency_contact_name || "-"}</p></div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">EC Phone</p>
                        {orderPatient.emergency_contact_phone
                          ? <ClickToCallPhone phone={orderPatient.emergency_contact_phone} patientId={orderPatient.id || orderPatient._id} />
                          : <p className="font-medium">-</p>}
                      </div>
                      <div><p className="text-xs text-slate-500 dark:text-slate-400">Relationship</p><p className="font-medium">{orderPatient.emergency_contact_relationship || "-"}</p></div>
                    </div>
                    <div className="space-y-3 border-t pt-4">
                      {[["Medical History", orderPatient.medical_history], ["Allergies", orderPatient.allergies], ["Current Medications", orderPatient.current_medications]].map(([label, val]) => (
                        <div key={label}><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</p><p className="text-sm bg-slate-800/50 rounded-lg p-3">{val || "Not provided"}</p></div>
                      ))}
                    </div>
                  </div>
                ) : <p className="text-slate-400 dark:text-slate-500 text-center py-8">Patient demographics not available</p>}
              </TabsContent>
              <TabsContent value="questionnaire">
                {questionnaire ? (
                  <div className="bg-slate-800/50 rounded-xl p-4 space-y-2" data-testid="questionnaire-answers">
                    {Object.entries(questionnaire.answers).map(([key, val]) => (
                      <div key={key} className="flex justify-between text-sm py-1 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
                        <span className="text-slate-500 dark:text-slate-400 capitalize">{key.replace(/_/g, " ")}</span>
                        <span className="text-slate-800 dark:text-slate-100 font-medium text-right max-w-[60%]">{val}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-slate-400 dark:text-slate-500 text-center py-8">Questionnaire not available</p>}
              </TabsContent>
            </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ERX ACTION MODAL ===== */}
      <Dialog open={!!actionModal} onOpenChange={() => { setActionModal(null); setDdiAck({ all_high_acknowledged: true, acked_keys: [], high_keys: [] }); setQReviewAcked(false); setActionQuestionnaire(null); setPrescribingLicense(null); setIdVerification(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Write ERX - {actionModal?.patient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="erx-form">
            {actionModal && (
              <DDIWarningBanner
                orderId={actionModal.id}
                requireAck
                onAcknowledgmentsChange={setDdiAck}
              />
            )}

            {/* ===== STEP 1 · IDENTITY VERIFICATION (LegitScript) =====
                Prescriber must confirm the patient's government-issued
                ID has been reviewed and marked "Verified" before the
                intake questionnaire gate unlocks. Server-side enforces
                this on PUT /orders/{id}/action (428 id_verification_required).
                Verified state auto-loads from /admin/users/{id}/id-verification. */}
            {actionModal && !idVerification?.verified && (
              <div
                className="rounded-xl border-2 border-rose-500 bg-rose-50 dark:bg-rose-900/20 p-4"
                data-testid="id-verification-gate"
              >
                <div className="flex items-start gap-2 mb-2">
                  <ShieldAlert className="w-5 h-5 text-rose-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-rose-900 dark:text-rose-200 text-sm">
                      Step 1 · Verify the patient's government ID
                    </p>
                    <p className="text-xs text-rose-800 dark:text-rose-300 mt-0.5">
                      LegitScript compliance — the patient's uploaded ID must be
                      reviewed and marked Verified before prescribing.
                    </p>
                  </div>
                </div>
                {loadingIdVerification ? (
                  <p className="text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading uploaded ID…
                  </p>
                ) : !idVerification?.has_id_card ? (
                  <p className="text-sm text-rose-800 dark:text-rose-200" data-testid="id-gate-none">
                    Patient has not uploaded a government ID yet. Route to CS to
                    prompt the patient before prescribing.
                  </p>
                ) : (
                  <>
                    <div className="rounded-lg border border-rose-300 bg-white dark:bg-slate-900 p-3 grid grid-cols-[110px_1fr] gap-3 text-xs mb-3" data-testid="id-gate-details">
                      <span className="font-semibold uppercase tracking-wider text-rose-600">ID type</span>
                      <span className="text-slate-800 dark:text-slate-100">{(idVerification.id_type || "—").replace(/_/g, " ")}</span>
                      <span className="font-semibold uppercase tracking-wider text-rose-600">DL ending</span>
                      <span className="text-slate-800 dark:text-slate-100 font-mono">•••• {idVerification.dl_last4 || "—"}</span>
                      <span className="font-semibold uppercase tracking-wider text-rose-600">State</span>
                      <span className="text-slate-800 dark:text-slate-100">{idVerification.dl_state || "—"}</span>
                      <span className="font-semibold uppercase tracking-wider text-rose-600">Uploaded</span>
                      <span className="text-slate-800 dark:text-slate-100">{idVerification.id_photo_uploaded_at ? new Date(idVerification.id_photo_uploaded_at).toLocaleString() : "—"}</span>
                    </div>
                    <a
                      href={idVerification.id_photo_url || `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${actionModal.patient_id}/id-verification/file/id-photo`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:text-rose-900 underline mb-3"
                      data-testid="id-gate-view-photo"
                    >
                      <Eye className="w-3.5 h-3.5" /> View uploaded ID photo
                    </a>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await API.post(`/admin/users/${actionModal.patient_id}/id-verification/verify`, { verified: true, notes: "Verified by prescriber via eRx modal Step 1" });
                          toast.success("Patient ID marked verified");
                          const { data } = await API.get(`/admin/users/${actionModal.patient_id}/id-verification`);
                          setIdVerification(data);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Could not mark verified — try the ID tab on the patient profile.");
                        }
                      }}
                      className="w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm py-2.5 flex items-center justify-center gap-2 transition-colors"
                      data-testid="id-gate-verify-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark ID as Verified
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Compact chip after ID has been verified so the prescriber
                can still confirm at a glance while doing the intake review. */}
            {actionModal && idVerification?.verified && (
              <div
                className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs"
                data-testid="id-verified-chip"
              >
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-800 dark:text-emerald-200 font-semibold">
                  Step 1 · Patient ID verified
                </span>
                {idVerification.verified_at && (
                  <span className="text-emerald-700 dark:text-emerald-300">
                    · {new Date(idVerification.verified_at).toLocaleDateString()}
                  </span>
                )}
                <a
                  href={idVerification.id_photo_url || `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${actionModal.patient_id}/id-verification/file/id-photo`}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto text-emerald-700 dark:text-emerald-300 underline hover:no-underline"
                  data-testid="id-verified-view-photo"
                >
                  View ID
                </a>
              </div>
            )}

            {/* ===== INTAKE QUESTIONNAIRE REVIEW GATE (LegitScript) =====
                The prescriber MUST scroll through the full questionnaire
                and click "Reviewed" before the Approve/Deny buttons at
                the bottom unlock. Server-side also enforces this on
                PUT /orders/{id}/action (428 Precondition Required).
                Only shown once Step 1 (ID verification) is done. */}
            {actionModal && idVerification?.verified && !qReviewAcked && (
              <div
                className="rounded-xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-4"
                data-testid="questionnaire-review-gate"
              >
                <div className="flex items-start gap-2 mb-2">
                  <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                      Step 2 · Review the intake questionnaire before prescribing
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                      Confirm you've read every answer below (including current medications,
                      contraindications, and allergies). This attestation is time-stamped and
                      logged to the patient record.
                    </p>
                  </div>
                </div>

                <div
                  className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-amber-300 bg-white dark:bg-slate-900 p-3 text-sm"
                  data-testid="questionnaire-review-answers"
                >
                  {actionQuestionnaire?.answers ? (
                    Object.entries(actionQuestionnaire.answers).map(([q, a]) => (
                      <div key={q} className="mb-2 last:mb-0">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{q}</p>
                        <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                          {typeof a === "boolean" ? (a ? "Yes" : "No") : (a ?? "—")}
                        </p>
                      </div>
                    ))
                  ) : actionQuestionnaire ? (
                    <p className="text-slate-500 dark:text-slate-400 text-center py-4">No questionnaire on file for this order.</p>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400 text-center py-4">Loading questionnaire…</p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={!actionQuestionnaire?.answers || markingQReviewed}
                  data-testid="questionnaire-reviewed-btn"
                  onClick={async () => {
                    if (!actionModal?.id) return;
                    setMarkingQReviewed(true);
                    try {
                      await API.post(`/orders/${actionModal.id}/questionnaire-reviewed`);
                      setQReviewAcked(true);
                      // Update the local order snapshot so re-opens don't re-block
                      setActionModal((prev) => prev ? {
                        ...prev,
                        questionnaire_reviewed: true,
                        questionnaire_reviewed_at: new Date().toISOString(),
                        questionnaire_reviewed_by_name: user?.name || "",
                      } : prev);
                      toast.success("Questionnaire review recorded");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Could not record review — try again");
                    } finally {
                      setMarkingQReviewed(false);
                    }
                  }}
                  className="mt-3 w-full rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm py-2.5 flex items-center justify-center gap-2 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  {markingQReviewed ? "Recording…" : "I have reviewed the intake questionnaire"}
                </button>
              </div>
            )}

            {/* Compact "reviewed" chip after acknowledgment so the prescriber
                can still verify at a glance while the eRx form is active. */}
            {actionModal && qReviewAcked && (
              <div
                className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs"
                data-testid="questionnaire-reviewed-chip"
              >
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-800 dark:text-emerald-200 font-semibold">
                  Step 2 · Questionnaire reviewed
                </span>
                {actionModal.questionnaire_reviewed_by_name && (
                  <span className="text-emerald-700 dark:text-emerald-300">
                    · {actionModal.questionnaire_reviewed_by_name}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setQReviewAcked(false)}
                  className="ml-auto text-emerald-700 dark:text-emerald-300 underline hover:no-underline"
                  data-testid="questionnaire-review-reopen"
                >
                  Re-open
                </button>
              </div>
            )}

            {/* ===== PRESCRIBING UNDER (auto-selected state license) =====
                Server auto-selects the prescriber's active license that
                matches the patient's ship-to state and embeds THAT
                license's number + NPI on the eRx. This panel is a
                read-only visual confirmation so the clinician sees
                exactly which state license will be stamped before
                clicking Approve. */}
            {actionModal && (
              <div
                className="rounded-xl border border-indigo-300 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4"
                data-testid="prescribing-license-panel"
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileCheck className="w-4 h-4 text-indigo-700 dark:text-indigo-300 shrink-0" />
                  <p className="text-xs font-bold uppercase tracking-widest text-indigo-800 dark:text-indigo-200">
                    Prescribing under
                  </p>
                </div>
                {loadingLicense ? (
                  <p className="text-sm text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Matching your state license…
                  </p>
                ) : prescribingLicense?.has_active_license && prescribingLicense.license ? (
                  <div className="grid grid-cols-3 gap-3 text-sm" data-testid="prescribing-license-details">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">Patient state</p>
                      <p className="font-bold text-indigo-900 dark:text-indigo-100" data-testid="prescribing-license-state">
                        {prescribingLicense.patient_state || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">License #</p>
                      <p className="font-mono font-bold text-indigo-900 dark:text-indigo-100" data-testid="prescribing-license-number">
                        {prescribingLicense.license.license_number}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">Expires</p>
                      <p className={`font-semibold ${prescribingLicense.license.expiring_soon ? "text-amber-700 dark:text-amber-300" : "text-indigo-900 dark:text-indigo-100"}`} data-testid="prescribing-license-expires">
                        {prescribingLicense.license.expiration_date}
                        {prescribingLicense.license.expiring_soon && " ⚠"}
                      </p>
                    </div>
                    {prescribingLicense.license.npi_number && (
                      <div className="col-span-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">NPI (state-specific)</p>
                        <p className="font-mono text-xs text-indigo-800 dark:text-indigo-200" data-testid="prescribing-license-npi">
                          {prescribingLicense.license.npi_number}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 text-sm text-rose-800 dark:text-rose-200" data-testid="prescribing-license-missing">
                    {prescribingLicense?.reason === "ship_state_unknown" ? (
                      <>
                        We couldn&apos;t determine the patient&apos;s state from this order (missing shipping address + profile state). Ask an admin to update the patient&apos;s address so the correct license can match.
                      </>
                    ) : (
                      <>
                        You have no active license on file for{" "}
                        <span className="font-bold">{prescribingLicense?.patient_state || "this patient's state"}</span>.
                        Contact an admin to add the license before prescribing.
                      </>
                    )}
                  </div>
                )}
              </div>
            )}


            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <p className="text-xs text-purple-600 font-bold uppercase tracking-widest mb-2">Patient Order</p>
              <p className="font-medium text-slate-900 dark:text-white">{actionModal?.patient_name} - {actionModal?.patient_email}</p>
              {actionModal?.patient_display_id && (
                <p className="text-xs mt-0.5 text-purple-700 font-semibold" data-testid="erx-modal-patient-id">
                  Patient ID: <span className="font-mono">{actionModal.patient_display_id}</span>
                </p>
              )}
              <p className="text-sm text-slate-600 dark:text-slate-300">Requested: {actionModal?.medication_name} ({actionModal?.medication_type})</p>

              {/* Requested strength — always visible so prescriber sees what to prescribe at a glance */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className={`text-sm font-semibold rounded-md px-2.5 py-1 inline-flex items-center gap-1.5 border ${
                    actionModal?.dose_request_above_starter
                      ? "text-amber-900 bg-amber-50 border-amber-300"
                      : "text-purple-800 bg-white border-purple-200"
                  }`}
                  data-testid="erx-modal-requested-strength"
                  title={
                    actionModal?.requested_strength_source === "previous_rx"
                      ? "Patient currently on this strength"
                      : actionModal?.requested_strength_source === "previous_request"
                        ? "Carried from prior order"
                        : actionModal?.requested_strength_source === "patient_request"
                          ? "Patient self-selected this strength"
                          : "Default starter dose"
                  }
                >
                  <Pill className="w-3.5 h-3.5" />
                  Patient requested: <span className="font-bold">{actionModal?.requested_strength || "—"}</span>
                  {actionModal?.requested_strength_source === "starter_default" && (
                    <span className="font-normal text-purple-600 ml-1">(starter)</span>
                  )}
                  {actionModal?.requested_strength_source === "previous_rx" && (
                    <span className="font-normal text-purple-600 ml-1">(current dose)</span>
                  )}
                </span>
                {actionModal?.starter_strength && actionModal?.starter_strength !== actionModal?.requested_strength && (
                  <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                    Safe starter: <span className="font-semibold text-slate-700 dark:text-slate-200">{actionModal.starter_strength}</span>
                  </span>
                )}
              </div>

              {/* Above-starter dose request — prominent amber callout with the patient's written justification */}
              {actionModal?.dose_request_above_starter && (
                <div
                  className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-50/80 p-3"
                  data-testid="above-starter-request-block"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0" />
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                      Above starter dose — patient documentation
                    </p>
                    {actionModal?.dose_request_acknowledged && (
                      <span
                        className="text-[10px] font-semibold text-amber-900 bg-amber-200 border border-amber-300 rounded-full px-1.5 py-0.5"
                        title="Patient checked the $25 retained-fee acknowledgement at checkout"
                        data-testid="above-starter-acknowledged-chip"
                      >
                        $25-risk acknowledged
                      </span>
                    )}
                  </div>
                  {actionModal?.above_starter_documentation ? (
                    typeof actionModal.above_starter_documentation === "object" ? (
                      <div
                        className="bg-white dark:bg-slate-900 rounded-lg border border-amber-200 divide-y divide-amber-100 dark:divide-amber-900/30 overflow-hidden"
                        data-testid="above-starter-documentation"
                      >
                        {[
                          { k: "pharmacy",   label: "Pharmacy" },
                          { k: "prescriber", label: "Prescriber" },
                          { k: "dose",       label: "Last dose" },
                          { k: "duration",   label: "Duration" },
                          { k: "notes",      label: "Notes" },
                        ].map(({ k, label }) => {
                          const v = actionModal.above_starter_documentation[k];
                          if (!v) return null;
                          return (
                            <div key={k} className="grid grid-cols-[110px_1fr] gap-2 px-3 py-1.5" data-testid={`above-starter-doc-${k}`}>
                              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 self-center">{label}</span>
                              <span className="text-sm text-slate-800 dark:text-slate-100">{v}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p
                        className="text-sm text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-900 rounded-lg p-3 border border-amber-200 whitespace-pre-wrap"
                        data-testid="above-starter-documentation"
                      >
                        {actionModal.above_starter_documentation}
                      </p>
                    )
                  ) : (
                    <p className="text-xs italic text-amber-700">
                      No written documentation was captured (legacy order — added{" "}
                      <code className="text-[10px]">above_starter_documentation</code>{" "}
                      requirement after this order was placed).
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Patient Note Alert */}
            {actionModal?.patient_note && (
              <div className={`rounded-xl p-4 border ${actionModal?.patient_note_acknowledged ? "bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800" : "bg-amber-50 border-amber-300"}`} data-testid="patient-note-alert">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className={`w-4 h-4 ${actionModal?.patient_note_acknowledged ? "text-slate-400 dark:text-slate-500" : "text-amber-600"}`} />
                  <p className={`text-xs font-bold uppercase tracking-widest ${actionModal?.patient_note_acknowledged ? "text-slate-400 dark:text-slate-500" : "text-amber-700"}`}>
                    Patient Note {actionModal?.patient_note_acknowledged ? "(Acknowledged)" : "— Must Acknowledge"}
                  </p>
                </div>
                <p className="text-sm text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-900 rounded-lg p-3 border border-slate-200 dark:border-slate-800">{actionModal.patient_note}</p>
                {!actionModal?.patient_note_acknowledged && (
                  <Button size="sm" className="mt-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-slate-900 dark:text-white" onClick={async () => {
                    try {
                      await API.put(`/orders/${actionModal.id}/acknowledge-note`);
                      setActionModal({ ...actionModal, patient_note_acknowledged: true });
                      toast.success("Patient note acknowledged");
                    } catch { toast.error("Failed to acknowledge"); }
                  }} data-testid="acknowledge-note-btn">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Acknowledge Note
                  </Button>
                )}
              </div>
            )}

            {/* Rx Templates */}
            {rxTemplates.length > 0 && (
            <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 border border-slate-300 dark:border-slate-700" data-testid="rx-templates-section">
              <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-3">Quick Rx Templates</p>
              {[{ key: "semaglutide", label: "Semaglutide with B12", list: groupedTemplates.semaglutide }, { key: "tirzepatide", label: "Tirzepatide with B12", list: groupedTemplates.tirzepatide }].map(({ key, label, list }) => {
                if (list.length === 0) return null;
                return (
                  <div key={key} className="mb-3 last:mb-0">
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase mb-2">{label}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {list.map(t => (
                        <button key={t.id} onClick={() => { setSelectedRxTemplateId(t.id); setErxForm({
                          ...erxForm,
                          medication_name: t.medication_name,
                          dosage: t.dosage,
                          quantity: t.quantity,
                          quantity_numeric: t.quantity_numeric || 1,
                          quantity_unit: t.quantity_unit || "mL",
                          route_form: t.route_form || "Subcutaneous Injection",
                          refills: t.refills || "0",
                          directions: t.directions,
                          diagnosis: erxForm.diagnosis || t.diagnosis || "",
                          rx_notes: erxForm.rx_notes || t.rx_notes || "",
                        }); }}
                          className={`text-left rounded-lg p-2.5 border transition-all ${
                            selectedRxTemplateId === t.id
                              ? "bg-gradient-to-br from-purple-600 to-purple-500 text-white border-purple-400 ring-2 ring-purple-300 shadow-lg shadow-purple-500/30 -translate-y-0.5"
                              : "bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-500"
                          }`}
                          data-testid={`rx-tpl-${t.id}`}>
                          <p className={`text-xs font-semibold ${selectedRxTemplateId === t.id ? "text-white" : "text-slate-900 dark:text-white"}`}>{t.name}</p>
                          <p className={`text-[10px] ${selectedRxTemplateId === t.id ? "text-purple-100" : "text-slate-400 dark:text-slate-500"}`}>{t.dosage} &middot; {t.quantity}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* Other category templates */}
              {groupedTemplates.other.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase mb-2">Other</p>
                  <div className="grid grid-cols-2 gap-2">
                    {groupedTemplates.other.map(t => (
                      <button key={t.id} onClick={() => { setSelectedRxTemplateId(t.id); setErxForm({
                        ...erxForm,
                        medication_name: t.medication_name,
                        dosage: t.dosage,
                        quantity: t.quantity,
                        refills: t.refills || "0",
                        directions: t.directions,
                        diagnosis: erxForm.diagnosis || t.diagnosis || "",
                        rx_notes: erxForm.rx_notes || t.rx_notes || "",
                      }); }}
                        className={`text-left rounded-lg p-2.5 border transition-all ${
                          selectedRxTemplateId === t.id
                            ? "bg-gradient-to-br from-purple-600 to-purple-500 text-white border-purple-400 ring-2 ring-purple-300 shadow-lg shadow-purple-500/30 -translate-y-0.5"
                            : "bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-500"
                        }`}
                        data-testid={`rx-tpl-${t.id}`}>
                        <p className={`text-xs font-semibold ${selectedRxTemplateId === t.id ? "text-white" : "text-slate-900 dark:text-white"}`}>{t.name}</p>
                        <p className={`text-[10px] ${selectedRxTemplateId === t.id ? "text-purple-100" : "text-slate-400 dark:text-slate-500"}`}>{t.dosage} &middot; {t.quantity}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Dose Presets for 90-day / Royal tier orders */}
            {dosePresets.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-testid="dose-presets-section">
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">90-Day Dose Presets (3 Vials)</p>
                <div className="grid grid-cols-2 gap-2">
                  {dosePresets.map(preset => (
                    <button key={preset.id} onClick={() => setErxForm({
                      ...erxForm,
                      dosage: `Vial 1: ${preset.vial_1_strength}, Vial 2: ${preset.vial_2_strength}, Vial 3: ${preset.vial_3_strength}`,
                      quantity: "3 vials",
                      directions: `${preset.name}: ${preset.description || ''}. Vial 1 (Month 1): ${preset.vial_1_strength}, Vial 2 (Month 2): ${preset.vial_2_strength}, Vial 3 (Month 3): ${preset.vial_3_strength}`,
                    })}
                      className="text-left bg-white dark:bg-slate-900 border border-amber-200 rounded-lg p-2.5 hover:bg-amber-50 transition-colors"
                      data-testid={`preset-${preset.id}`}>
                      <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{preset.name}</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">{preset.vial_1_strength} / {preset.vial_2_strength} / {preset.vial_3_strength}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Medication Name</Label>
                <Input value={erxForm.medication_name} onChange={(e) => setErxForm({ ...erxForm, medication_name: e.target.value })} className="mt-1 rounded-xl" data-testid="erx-med-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Dosage</Label>
                <Input value={erxForm.dosage} onChange={(e) => setErxForm({ ...erxForm, dosage: e.target.value })} placeholder="e.g. 0.25mg/0.5mL" className="mt-1 rounded-xl" data-testid="erx-dosage" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Quantity</Label>
                <Input value={erxForm.quantity} onChange={(e) => setErxForm({ ...erxForm, quantity: e.target.value })} placeholder="e.g. 1 vial (3mL)" className="mt-1 rounded-xl" data-testid="erx-quantity" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Refills</Label>
                <Input value={erxForm.refills} onChange={(e) => setErxForm({ ...erxForm, refills: e.target.value })} placeholder="0" className="mt-1 rounded-xl" data-testid="erx-refills" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Route / Form</Label>
                <Input value={erxForm.route_form} onChange={(e) => setErxForm({ ...erxForm, route_form: e.target.value })} placeholder="e.g. Subcutaneous Injection" className="mt-1 rounded-xl" data-testid="erx-route-form" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Unit</Label>
                <Input value={erxForm.quantity_unit} onChange={(e) => setErxForm({ ...erxForm, quantity_unit: e.target.value })} placeholder="e.g. mL" className="mt-1 rounded-xl" data-testid="erx-qty-unit" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Directions (SIG)</Label>
              <Textarea value={erxForm.directions} onChange={(e) => setErxForm({ ...erxForm, directions: e.target.value })} placeholder="e.g. Inject 0.25mg subcutaneously once weekly" className="mt-1 rounded-xl" data-testid="erx-directions" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Diagnosis / Conditions</Label>
              <Input value={erxForm.diagnosis} onChange={(e) => setErxForm({ ...erxForm, diagnosis: e.target.value })} placeholder="e.g. Obesity (E66.01), Type 2 Diabetes (E11.65)" className="mt-1 rounded-xl" data-testid="erx-diagnosis" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Dispensing Pharmacy</Label>
              <Select value={actionPharmacy} onValueChange={setActionPharmacy}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="erx-pharmacy-select"><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
                <SelectContent>
                  {pharmacies.map((p) => {
                    const isPreferred = (p.slug || "").toLowerCase() === "polomar";
                    return (
                      <SelectItem key={p.id} value={p.id} data-testid={`erx-pharmacy-opt-${p.slug || p.id}`}>
                        {p.name}{p.location ? ` - ${p.location}` : ""}{isPreferred ? " · Preferred" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {((pharmacies || []).find((p) => p.id === actionPharmacy)?.slug || "").toLowerCase() === "polomar" && (
                <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-1" data-testid="erx-polomar-hint">
                  Approving will auto-push this eRx to Polomar via LifeFile.
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Rx Notes (appears on prescription)</Label>
                <div className="flex items-center gap-2">
                  {dispenseTemplates.length > 0 && (
                    <Select
                      value=""
                      onValueChange={(v) => {
                        const tpl = dispenseTemplates.find((t) => t.id === v);
                        if (!tpl) return;
                        const sep = erxForm.rx_notes ? "\n" : "";
                        setErxForm({ ...erxForm, rx_notes: (erxForm.rx_notes || "") + sep + tpl.body });
                        toast.success(`Appended: ${tpl.name}`);
                      }}
                    >
                      <SelectTrigger className="rounded-lg h-8 text-xs px-2" data-testid="dispense-template-trigger">
                        <SelectValue placeholder="+ Append dispense template" />
                      </SelectTrigger>
                      <SelectContent>
                        {dispenseTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`dispense-template-${t.id}`}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <RxNoteSaver value={erxForm.rx_notes} onSelect={(v) => setErxForm({ ...erxForm, rx_notes: v })} />
                </div>
              </div>
              <Textarea value={erxForm.rx_notes} onChange={(e) => setErxForm({ ...erxForm, rx_notes: e.target.value })} placeholder="Notes for pharmacy / prescription fill instructions..." className="mt-1 rounded-xl" rows={2} data-testid="erx-rx-notes" />
            </div>

            {/* Ship-to Toggle */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4" data-testid="ship-to-section">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Ship This Order To</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShipToClinic(false)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${!shipToClinic ? "bg-purple-600 text-white border-purple-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700"}`}
                  data-testid="ship-to-patient-btn"
                >
                  Ship to Patient
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (myClinics.length === 0) {
                      toast.info("Add a clinic in 'My Clinics' first.");
                      return;
                    }
                    setShipToClinic(true);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${shipToClinic ? "bg-amber-600 text-white border-amber-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700"}`}
                  data-testid="ship-to-clinic-btn"
                >
                  Ship to Clinic
                </button>
              </div>
              {shipToClinic && (
                <div className="mt-3">
                  <Label className="text-xs text-slate-600 dark:text-slate-400">Select Clinic</Label>
                  <Select value={shipClinicId} onValueChange={setShipClinicId}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="ship-clinic-select"><SelectValue placeholder="Pick a clinic" /></SelectTrigger>
                    <SelectContent>
                      {myClinics.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`ship-clinic-opt-${c.id}`}>
                          {c.name} — {c.city}, {c.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1.5">
                    The medication will be dispensed to your clinic for in-office administration. Patient name will be tagged &quot;c/o {(myClinics.find((c) => c.id === shipClinicId)?.name) || "(select clinic)"}&quot;.
                  </p>
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Internal Notes (patient-facing, not on ERX)</Label>
              <Textarea value={erxForm.notes} onChange={(e) => setErxForm({ ...erxForm, notes: e.target.value })} placeholder="Additional notes for the patient" className="mt-1 rounded-xl" rows={2} data-testid="erx-notes" />
            </div>

            {/* Sign-off attestation: opt-in to use saved e-signature */}
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 dark:bg-purple-950/20 dark:border-purple-900/50 p-3" data-testid="signoff-attestation-section">
              {savedSignature ? (
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={useSavedSignature}
                    onChange={(e) => setUseSavedSignature(e.target.checked)}
                    className="w-4 h-4 accent-purple-600"
                    data-testid="use-saved-signature-checkbox"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      Sign with my saved e-signature
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Attaches your touch-signature (from Employee Profile) to the eRx audit record.
                    </p>
                  </div>
                  {useSavedSignature && (
                    <img
                      src={savedSignature}
                      alt="Signature preview"
                      className="h-10 border rounded bg-white p-1"
                      data-testid="saved-signature-preview"
                    />
                  )}
                </label>
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Tip: save a touch signature in <strong>My Profile → Signature</strong> and it&apos;ll appear here as a one-click sign-off option.
                </p>
              )}
            </div>
          </div>
          {denyMode && (
            <div className="mt-4 rounded-xl border-2 border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-800 p-4" data-testid="deny-reason-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-rose-800 dark:text-rose-300">Why are you denying this order?</p>
                <button
                  type="button"
                  onClick={() => { setDenyMode(false); setDenyReason(""); setDenyMissingFields([]); setDenyExtra(""); }}
                  className="text-xs text-rose-700 hover:underline"
                  data-testid="deny-cancel-btn"
                >
                  Cancel
                </button>
              </div>
              <Select value={denyReason} onValueChange={setDenyReason}>
                <SelectTrigger className="rounded-lg bg-white dark:bg-slate-900" data-testid="deny-reason-select">
                  <SelectValue placeholder="Pick a reason…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insufficient_documentation" data-testid="deny-reason-insufficient_documentation">Insufficient documentation (above-starter request)</SelectItem>
                  <SelectItem value="above_starter_clinical" data-testid="deny-reason-above_starter_clinical">Clinical guidelines — start at safe starter dose</SelectItem>
                  <SelectItem value="missing_labs" data-testid="deny-reason-missing_labs">Missing recent labs</SelectItem>
                  <SelectItem value="out_of_state" data-testid="deny-reason-out_of_state">Not licensed in patient&apos;s state</SelectItem>
                  <SelectItem value="other" data-testid="deny-reason-other">Other (use notes above)</SelectItem>
                </SelectContent>
              </Select>

              {denyReason === "insufficient_documentation" && (
                <div className="mt-3" data-testid="deny-missing-fields-block">
                  <p className="text-xs font-semibold text-rose-800 dark:text-rose-300 mb-1.5">Which documentation fields need more detail? (Tick all that apply)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { k: "pharmacy",   label: "Pharmacy" },
                      { k: "prescriber", label: "Prescriber" },
                      { k: "dose",       label: "Last dose" },
                      { k: "duration",   label: "Duration" },
                    ].map(({ k, label }) => (
                      <label key={k} className="flex items-center gap-2 text-sm text-rose-900 dark:text-rose-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={denyMissingFields.includes(k)}
                          onChange={(e) => setDenyMissingFields((prev) =>
                            e.target.checked ? [...prev, k] : prev.filter((x) => x !== k)
                          )}
                          className="w-4 h-4 accent-rose-600"
                          data-testid={`deny-missing-${k}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={denyExtra}
                    onChange={(e) => setDenyExtra(e.target.value)}
                    placeholder="Optional extra note for the patient…"
                    maxLength={200}
                    className="mt-2 w-full text-sm rounded-lg border-2 border-rose-200 bg-white dark:bg-slate-900 px-3 py-1.5 focus:outline-none focus:border-rose-500"
                    data-testid="deny-extra-note"
                  />
                  <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-2">
                    The patient will receive an auto-formatted email asking them to resubmit with the ticked fields filled in. The $25 consultation fee from this denial is credited to their re-order.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 mt-4">
            {!denyMode ? (
              <Button
                variant="outline"
                className="rounded-lg text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  setDenyMode(true);
                  // Pre-populate the missing-fields list with whichever docs
                  // are actually blank/short so the prescriber has a head start.
                  const doc = actionModal?.above_starter_documentation;
                  if (doc && typeof doc === "object") {
                    setDenyMissingFields(
                      ["pharmacy", "prescriber", "dose", "duration"].filter(
                        (k) => !doc[k] || String(doc[k]).trim().length < 3
                      )
                    );
                    if (actionModal?.dose_request_above_starter) setDenyReason("insufficient_documentation");
                  }
                }}
                disabled={processing || !qReviewAcked}
                data-testid="open-deny-btn"
                title={!qReviewAcked ? "Review the intake questionnaire first" : undefined}
              >
                <XCircle className="w-4 h-4 mr-1" /> Deny Order
              </Button>
            ) : (
              <Button
                variant="outline"
                className="rounded-lg text-white bg-red-600 border-red-700 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleAction("deny")}
                disabled={processing || !qReviewAcked || !denyReason || (denyReason === "insufficient_documentation" && denyMissingFields.length === 0)}
                data-testid="confirm-deny-btn"
                title={!qReviewAcked ? "Review the intake questionnaire first" : (!denyReason ? "Pick a reason first" : (denyReason === "insufficient_documentation" && denyMissingFields.length === 0 ? "Tick at least one field that needs more detail" : undefined))}
              >
                <XCircle className="w-4 h-4 mr-1" /> {processing ? "Sending…" : "Confirm Denial"}
              </Button>
            )}
            <Button
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleAction("approve")}
              disabled={processing || !ddiAck.all_high_acknowledged || !qReviewAcked}
              data-testid="confirm-approve-btn"
              title={!qReviewAcked ? "Review the intake questionnaire first" : (!ddiAck.all_high_acknowledged ? "Acknowledge each HIGH-severity interaction warning above before approving." : undefined)}
            >
              <Send className="w-4 h-4 mr-1" /> {processing ? "Sending..." : (!qReviewAcked ? "Review questionnaire first" : (ddiAck.all_high_acknowledged ? "Approve & Send ERX" : `Acknowledge ${ddiAck.high_keys.filter(k => !ddiAck.acked_keys.includes(k)).length} HIGH warning(s) first`))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== RX SERIES FOLLOW-UP MODAL ===== */}
      <RxSeriesModal
        open={!!seriesModalOrder}
        order={seriesModalOrder}
        onClose={() => setSeriesModalOrder(null)}
        onGenerated={() => { loadData(); }}
      />

      {/* ===== PATIENT DETAIL MODAL ===== */}
      <Dialog open={!!selectedPatient} onOpenChange={() => { setSelectedPatient(null); setPatientDetail(null); setPatientEditMode(false); setPatientEditForm({}); setExpandedOrderId(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4 pr-6">
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="text-slate-800 dark:text-slate-100">Patient Record - {selectedPatient?.name}</DialogTitle>
              {!isCS && (
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none" data-testid="patient-edit-toggle">
                  <input
                    type="checkbox"
                    checked={patientEditMode}
                    onChange={(e) => {
                      setPatientEditMode(e.target.checked);
                      if (e.target.checked) {
                        setPatientEditForm({
                          name: selectedPatient.name || "",
                          email: selectedPatient.email || "",
                          phone: selectedPatient.phone || "",
                          date_of_birth: selectedPatient.date_of_birth || "",
                          gender: selectedPatient.gender || "",
                          address_street: selectedPatient.address_street || "",
                          address_line2: selectedPatient.address_line2 || "",
                          address_city: selectedPatient.address_city || "",
                          address_state: selectedPatient.address_state || "",
                          address_zip: selectedPatient.address_zip || "",
                          emergency_contact_name: selectedPatient.emergency_contact_name || "",
                          emergency_contact_phone: selectedPatient.emergency_contact_phone || "",
                          emergency_contact_relationship: selectedPatient.emergency_contact_relationship || "",
                          medical_history: selectedPatient.medical_history || "",
                          allergies: selectedPatient.allergies || "",
                          current_medications: selectedPatient.current_medications || "",
                        });
                      } else {
                        setPatientEditForm({});
                      }
                    }}
                    className="rounded border-slate-600 bg-slate-100 dark:bg-slate-800"
                  />
                  {patientEditMode ? <Unlock className="w-3.5 h-3.5 text-amber-400" /> : <Lock className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />}
                  <span>{patientEditMode ? "Unlocked — click Save to apply" : "Unlock to edit"}</span>
                </label>
              )}
            </div>
          </DialogHeader>
          {selectedPatient && patientDetail && (
            <Tabs defaultValue="demographics" className="w-full" data-testid="patient-detail-tabs">
              <TabsList className="grid grid-cols-3 mb-4 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700">
                <TabsTrigger value="demographics" className="data-[state=active]:bg-purple-600 data-[state=active]:text-slate-900 dark:text-white">Demographics</TabsTrigger>
                <TabsTrigger value="orders" className="data-[state=active]:bg-purple-600 data-[state=active]:text-slate-900 dark:text-white">Orders ({patientDetail.orders?.length || 0})</TabsTrigger>
                <TabsTrigger value="questionnaires" className="data-[state=active]:bg-purple-600 data-[state=active]:text-slate-900 dark:text-white">Questionnaires ({patientDetail.questionnaires?.length || 0})</TabsTrigger>
              </TabsList>
              <TabsContent value="demographics">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      ["Name", "name"], ["Email", "email"], ["Phone", "phone"],
                      ["DOB", "date_of_birth"], ["Gender", "gender"],
                    ].map(([label, key]) => (
                      <div key={key}>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">{label}</p>
                        {patientEditMode ? (
                          <Input
                            value={patientEditForm[key] || ""}
                            onChange={(e) => setPatientEditForm({ ...patientEditForm, [key]: e.target.value })}
                            className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100"
                            data-testid={`pt-edit-${key}`}
                          />
                        ) : (
                          <p className="font-medium text-slate-800 dark:text-slate-100">{selectedPatient[key] || "-"}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-300 dark:border-slate-700 pt-4">
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">Address</p>
                    {patientEditMode ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={patientEditForm.address_street || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, address_street: e.target.value })} placeholder="Street" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                          <Input value={patientEditForm.address_line2 || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, address_line2: e.target.value })} placeholder="Apt / Suite / Unit / Lot (optional)" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" data-testid="pt-edit-address-line2" />
                          <Input value={patientEditForm.address_city || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, address_city: e.target.value })} placeholder="City" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                          <Input value={patientEditForm.address_state || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, address_state: e.target.value })} placeholder="State (e.g. FL)" maxLength={2} className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                          <Input value={patientEditForm.address_zip || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, address_zip: e.target.value })} placeholder="ZIP" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                        </div>
                        <AddressVerifyCard
                          street={patientEditForm.address_street}
                          city={patientEditForm.address_city}
                          state={patientEditForm.address_state}
                          zip={patientEditForm.address_zip}
                          resetSignal={`${patientEditForm.address_street}|${patientEditForm.address_city}|${patientEditForm.address_state}|${patientEditForm.address_zip}`}
                          onStatusChange={setPatientAddrStatus}
                          onVerified={(n) => setPatientEditForm(p => ({
                            ...p,
                            address_city: n.city || p.address_city,
                            address_state: n.state || p.address_state,
                            address_zip: n.postal_code || p.address_zip,
                          }))}
                        />
                      </div>
                    ) : (
                      <p className="font-medium text-slate-800 dark:text-slate-100">{[selectedPatient.address_street, selectedPatient.address_line2, selectedPatient.address_city, selectedPatient.address_state, selectedPatient.address_zip].filter(Boolean).join(", ") || "-"}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-t border-slate-300 dark:border-slate-700 pt-4">
                    {[["Emergency Contact", "emergency_contact_name"], ["EC Phone", "emergency_contact_phone"], ["Relationship", "emergency_contact_relationship"]].map(([label, key]) => (
                      <div key={key}>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">{label}</p>
                        {patientEditMode ? (
                          <Input value={patientEditForm[key] || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, [key]: e.target.value })} className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                        ) : (
                          <p className="font-medium text-slate-800 dark:text-slate-100">{selectedPatient[key] || "-"}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 border-t border-slate-300 dark:border-slate-700 pt-4">
                    {[["Medical History", "medical_history"], ["Allergies", "allergies"], ["Current Medications", "current_medications"]].map(([label, key]) => (
                      <div key={key}>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">{label}</p>
                        {patientEditMode ? (
                          <Textarea value={patientEditForm[key] || ""} onChange={(e) => setPatientEditForm({ ...patientEditForm, [key]: e.target.value })} rows={2} className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100" />
                        ) : (
                          <p className="text-sm bg-slate-800/70 text-slate-700 dark:text-slate-200 rounded-lg p-3 border border-slate-300 dark:border-slate-700">{selectedPatient[key] || "Not provided"}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {patientEditMode && (
                    <div className="flex justify-end gap-2 pt-4 border-t border-slate-300 dark:border-slate-700">
                      <Button variant="outline" size="sm" className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-800" onClick={() => { setPatientEditMode(false); setPatientEditForm({}); }} data-testid="pt-edit-cancel">Cancel</Button>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-slate-900 dark:text-white" onClick={handlePatientSave} disabled={patientSavePending} data-testid="pt-edit-save">
                        <Save className="w-4 h-4 mr-1" /> {patientSavePending ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="orders">
                <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                  {/* Group linked-cycle orders together — a Royal 90 cycle 2
                      should collapse under its cycle-1 parent instead of
                      visually triple-stacking with sibling cycles. We sort
                      every order by created_at desc, then attach any child
                      (parent_order_id matches) to its parent's child array.
                      Orphan children (parent not in this patient's list)
                      stay top-level so we never hide an order. */}
                  {(() => {
                    const TIER_LABEL = {
                      one_time: "Monthly",
                    subscription: "Subscription",
                      solo_subscribe: "Solo Subscribe",
                      royal_90: "Royal 90",
                      pre_screened: "Pre-screened",
                    };
                    const all = patientDetail.orders || [];
                    const byId = new Map(all.map((o) => [o.id, o]));
                    const children = new Map();
                    const tops = [];
                    for (const o of all) {
                      if (o.parent_order_id && byId.has(o.parent_order_id)) {
                        const arr = children.get(o.parent_order_id) || [];
                        arr.push(o);
                        children.set(o.parent_order_id, arr);
                      } else {
                        tops.push(o);
                      }
                    }
                    const renderRow = (o, isChild = false) => {
                      const isOpen = expandedOrderId === o.id;
                      const tracking = o.tracking_number;
                      const carrier = o.carrier || "Unknown";
                      const tierLabel = TIER_LABEL[o.payment_type] || o.payment_type || null;
                      const strength = o.dosage_requested || o.medication_strength || null;
                      const cycles = children.get(o.id) || [];
                      const hasCycles = cycles.length > 0;
                      return (
                        <div
                          key={o.id}
                          className={`border ${
                            isChild
                              ? "border-slate-200 dark:border-slate-700 ml-6 bg-slate-50 dark:bg-slate-800/30"
                              : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800/60"
                          } rounded-lg`}
                          data-testid={`pt-order-${o.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedOrderId(isOpen ? null : o.id)}
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-800 transition-colors rounded-lg"
                          >
                            <div className="flex items-center gap-3 text-left min-w-0">
                              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" />}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-sm text-slate-800 dark:text-slate-100">{o.medication_name}</p>
                                  {strength && (
                                    <span
                                      className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600"
                                      data-testid={`pt-order-strength-${o.id}`}
                                    >
                                      {strength}
                                    </span>
                                  )}
                                  {tierLabel && (
                                    <span
                                      className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200 border border-purple-300 dark:border-purple-700/50"
                                      data-testid={`pt-order-tier-${o.id}`}
                                    >
                                      {tierLabel}
                                    </span>
                                  )}
                                  {hasCycles && (
                                    <span
                                      className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700/50"
                                      data-testid={`pt-order-subscription-${o.id}`}
                                      title={`${cycles.length} linked refill cycle(s) — click to expand`}
                                    >
                                      Active subscription · {cycles.length + 1} cycles
                                    </span>
                                  )}
                                  {o.series_position && o.total_in_series && o.total_in_series > 1 && (
                                    <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400">
                                      Cycle {o.series_position}/{o.total_in_series}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{new Date(o.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full border shrink-0 ml-2 ${STATUS_COLORS[o.status] || ""}`}>{o.status.replace(/_/g, " ")}</span>
                          </button>
                          {isOpen && (
                            <div className="border-t border-slate-300 dark:border-slate-700 p-4 space-y-3 text-sm">
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div><span className="text-slate-400 dark:text-slate-500">Order ID:</span> <span className="text-slate-700 dark:text-slate-200 font-mono">{o.id.slice(0, 10)}</span></div>
                                <div><span className="text-slate-400 dark:text-slate-500">Payment:</span> <span className="text-slate-700 dark:text-slate-200">{o.payment_status || "N/A"}</span></div>
                                <div><span className="text-slate-400 dark:text-slate-500">Tier:</span> <span className="text-slate-700 dark:text-slate-200">{tierLabel || "-"}</span></div>
                                <div><span className="text-slate-400 dark:text-slate-500">Strength:</span> <span className="text-slate-700 dark:text-slate-200">{strength || "-"}</span></div>
                                <div><span className="text-slate-400 dark:text-slate-500">Type:</span> <span className="text-slate-700 dark:text-slate-200">{o.medication_type || "-"}</span></div>
                                <div><span className="text-slate-400 dark:text-slate-500">Vials:</span> <span className="text-slate-700 dark:text-slate-200">{o.vial_count || o.supply_units || "-"}</span></div>
                              </div>
                              {o.erx_id && (
                                <div className="pt-2 border-t border-slate-300 dark:border-slate-700">
                                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">ERX sent to Polomar</p>
                                  <div className="flex items-center gap-2 text-xs">
                                    <FileText className="w-3 h-3 text-purple-400" />
                                    <span className="text-slate-700 dark:text-slate-200 font-mono">{o.erx_id}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-purple-600 dark:text-purple-300 hover:text-purple-800 dark:hover:text-white h-6 px-2"
                                      onClick={() => openErxPdf(o.erx_id)}
                                      data-testid={`pt-order-erx-pdf-${o.id}`}
                                    >
                                      View eRx PDF <ExternalLink className="w-3 h-3 ml-1" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                              <div className="pt-2 border-t border-slate-300 dark:border-slate-700">
                                <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">Shipping Tracking</p>
                                {tracking ? (
                                  <div className="flex items-center gap-2 text-xs">
                                    <Truck className="w-3 h-3 text-emerald-400" />
                                    <span className="text-slate-700 dark:text-slate-200 uppercase">{carrier}</span>
                                    {o.tracking_url ? (
                                      <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white underline font-mono">{tracking}</a>
                                    ) : (
                                      <span className="text-slate-700 dark:text-slate-200 font-mono">{tracking}</span>
                                    )}
                                    {o.shipped_at && <span className="text-slate-400 dark:text-slate-500">· shipped {new Date(o.shipped_at).toLocaleDateString()}</span>}
                                  </div>
                                ) : o.erx_id ? (
                                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">Awaiting Polomar shipment confirmation…</p>
                                ) : (
                                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">Order not yet sent to pharmacy.</p>
                                )}
                              </div>
                              {hasCycles && (
                                <div className="pt-2 border-t border-slate-300 dark:border-slate-700 space-y-2">
                                  <p className="text-xs text-slate-400 dark:text-slate-500">Linked refill cycles ({cycles.length})</p>
                                  {cycles.map((c) => renderRow(c, true))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    };
                    return tops.map((o) => renderRow(o));
                  })()}
                  {(!patientDetail.orders || patientDetail.orders.length === 0) && <p className="text-slate-400 dark:text-slate-500 text-center py-4">No orders</p>}
                </div>
              </TabsContent>
              <TabsContent value="questionnaires">
                <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                  {patientDetail.questionnaires?.map((q) => (
                    <div key={q.id} className="border border-slate-300 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/60">
                      <p className="text-sm font-medium mb-3 text-slate-800 dark:text-slate-100">Submitted {new Date(q.submitted_at).toLocaleString()}</p>
                      <div className="space-y-1.5 max-h-[36vh] overflow-y-auto pr-1">
                        {Object.entries(q.answers || {}).map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-3 text-xs py-1 border-b border-slate-700/50 last:border-0">
                            <span className="text-slate-400 dark:text-slate-500 capitalize flex-shrink-0">{k.replace(/_/g, " ")}</span>
                            <span className="text-slate-700 dark:text-slate-200 text-right break-words">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(!patientDetail.questionnaires || patientDetail.questionnaires.length === 0) && <p className="text-slate-400 dark:text-slate-500 text-center py-4">No questionnaires</p>}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Patient detail modal — opens from clicking a patient name in the order queue */}
      <PatientDetailModal open={!!patientDetailId} onClose={() => setPatientDetailId(null)} patientId={patientDetailId} initialTab={patientDetailTab} />

      {/* Keyboard shortcuts — only active on the Orders tab for prescribers.
          Power-user feature: T/J/K/Enter/A/D/Esc + ? overlay. */}
      <PrescriberKeyboardShortcuts
        enabled={activeTab === "orders" && (user?.role === "prescriber" || user?.role === "admin")}
        rowIds={displayedRowIds}
        focusedIdx={focusedIdx}
        onFocusIndexChange={setFocusedIdx}
        onToggleTriage={() => setTriageMode((v) => !v)}
        modalOpen={!!selectedOrder || !!actionModal}
        onCloseModal={() => { setSelectedOrder(null); setActionModal(null); }}
      />
    </div>
  );
}

/**
 * Animate a numeric value from 0 → target over `duration` ms using
 * requestAnimationFrame. Cheap (one rAF loop per stat), runs once on mount,
 * and short-circuits if the user has `prefers-reduced-motion` enabled so we
 * respect accessibility settings even on a pitch demo.
 */
function useCountUp(target, duration = 1100) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const reduce = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce || !Number.isFinite(target) || target === 0) {
      setDisplay(target || 0);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      // ease-out cubic so the number decelerates as it lands on the final value
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return display;
}

/**
 * Single stat tile inside the AI-screening KPI strip rendered at the top of
 * the Order Queue. Three of these sit side by side: count of pre-screened,
 * automation rate, and count requiring clinician review. Built as a small
 * helper here rather than a separate file because it's only used in one
 * place and reading the three side-by-side props inline keeps the markup
 * obvious. Accent drives the color of the headline number — emerald for
 * "ready to act", purple for the brand metric, amber for "needs attention".
 *
 * `value` may be a plain number OR a string ending in "%". When it's a "%"
 * string we split off the suffix and only animate the numeric part so the
 * unit stays visually anchored.
 */
function KpiStat({ value, label, accent = "purple", last = false, testid }) {
  const isPctString = typeof value === "string" && value.endsWith("%");
  const numericTarget = isPctString
    ? parseInt(value, 10)
    : (typeof value === "number" ? value : 0);
  const animated = useCountUp(numericTarget);
  const rendered = isPctString ? `${animated}%` : animated;
  const accentClass = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    purple: "text-purple-600 dark:text-purple-400",
    amber: "text-amber-600 dark:text-amber-400",
  }[accent] || "text-purple-600 dark:text-purple-400";
  return (
    <div
      data-testid={testid}
      className={`flex flex-col items-start justify-center px-4 py-2.5 min-w-[140px] ${
        last ? "" : "border-r border-slate-200 dark:border-slate-700"
      }`}
    >
      <span className={`text-2xl font-extrabold tabular-nums leading-none ${accentClass}`}>
        {rendered}
      </span>
      <span className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1 font-medium">
        {label}
      </span>
    </div>
  );
}


